<template>
    <div class="create-devis-wrapper ppv2-p-6" data-app="true">
        <!-- Main Header -->
        <header class="header-v3 ppv2-flex ppv2-justify-between ppv2-items-center xl:ppv2-flex-row ppv2-flex-col ppv2-gap-8">
            <div
                class="header-left xl:ppv2-w-max ppv2-w-full ppv2-flex xl:ppv2-justify-start ppv2-justify-between ppv2-gap-8 ppv2-items-center"
            >
                <p class="poppinsRegular ppv2-mb-0" :style="{ fontSize: normalizeFontSize(30) }">
                    {{ $t('devis.v3.create_title') }}
                </p>
                <button
                    class="ml-4 ppv2-flex ppv2-items-center ppv2-cursor-pointer ppv2-gap-4 ppv2-p-3 ppv2-h-[40px] ppv2-text-sm ppv2-rounded-xl ppv2-text-black ppv2-border-solid ppv2-border-black ppv2-border"
                    @click="togglePostIt"
                >
                    <FileMinus width="16" height="16" />
                    {{ postItVisible ? $t('devis.v3.hide_client_request') : $t('devis.v3.show_client_request') }}
                </button>
            </div>
            <div
                class="header-right xl:ppv2-w-max ppv2-w-full ppv2-flex ppv2-flex-row ppv2-flex-wrap ppv2-justify-end ppv2-gap-4 md:ppv2-items-center ppv2-items-end"
            >
                <button
                    class="ppv2-flex ppv2-items-center ppv2-cursor-pointer ppv2-gap-4 ppv2-p-3 ppv2-h-[40px] ppv2-text-sm ppv2-rounded-xl ppv2-text-black ppv2-border-solid ppv2-border-black ppv2-border"
                    @click="handlePreviewPDF"
                >
                    <FileText width="16" height="16" />
                    {{ $t('devis.v3.preview_pdf') }}
                </button>
                <!-- Devis verrouillé : « Enregistrer » désactivé (rien à enregistrer — la seule modif
                     permise, la case Option, se sauvegarde seule). Wrapper :title pour le tooltip. -->
                <span :class="{ 'quote-locked-btn': quoteLocked }" :title="lockTitle">
                    <button
                        class="ppv2-flex ppv2-items-center ppv2-cursor-pointer ppv2-gap-4 ppv2-p-3 ppv2-h-[40px] ppv2-text-sm ppv2-rounded-xl ppv2-text-black ppv2-border-solid ppv2-border-black ppv2-border"
                        @click="handleSave"
                        :disabled="saving || quoteLocked"
                    >
                        <Save width="16" height="16" />
                        {{ $t('devis.v3.save') }}
                    </button>
                </span>
                <!--
                    Envoi du devis : le bouton ouvre un choix de mode.
                    « Envoi simple » = mail de devis standard, inchangé.
                    « Envoi personnalisé » = popup de rédaction (DevisSendModal).
                -->
                <div class="ppv2-relative" ref="sendDropdown" :class="{ 'quote-locked-btn': quoteLocked }" :title="lockTitle">
                    <BaseSplitButton
                        :label="$t('devis.v3.send')"
                        :icon="Send"
                        :iconSize="17"
                        :showDropdown="showSendDropdown"
                        @click="handleSend"
                        @toggle="handleSend"
                    />

                    <div
                        v-if="showSendDropdown"
                        class="menuDropdown ppv2-absolute ppv2-right-0 ppv2-top-full ppv2-mt-2 ppv2-w-56 ppv2-bg-white ppv2-rounded-xl ppv2-shadow-xl ppv2-z-50 ppv2-border ppv2-border-gray-100 ppv2-overflow-hidden"
                    >
                        <button
                            @click="handleSimpleSend"
                            :disabled="sendingQuote"
                            class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 disabled:ppv2-opacity-50"
                        >
                            <Send :size="16" class="ppv2-text-gray-400" />
                            <span class="poppinsRegular ppv2-text-sm">{{ $t('devis.v3.send_simple') }}</span>
                        </button>
                        <button
                            @click="handleCustomSend"
                            :disabled="sendingQuote"
                            class="ppv2-w-full ppv2-text-left ppv2-px-4 ppv2-py-3 hover:ppv2-bg-gray-50 ppv2-transition-colors ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-text-gray-700 ppv2-border-t ppv2-border-gray-50 disabled:ppv2-opacity-50"
                        >
                            <FilePen :size="16" class="ppv2-text-gray-400" />
                            <span class="poppinsRegular ppv2-text-sm">{{ $t('devis.v3.send_custom') }}</span>
                        </button>
                    </div>
                </div>
                <button @click="handleBack">
                    <X width="17" height="17" />
                </button>
            </div>
        </header>

        <!-- Bandeau devis accepté : rappel du verrou + badge « Accepté ». La signature, elle,
             s'affiche dans le bloc « Signature » en bas du devis. La case Option reste active. -->
        <div v-if="quoteLocked" class="quote-locked-banner" role="status">
            <Lock :size="16" />
            <span class="quote-accepted-badge">{{ $t('devis.v3.accepted_badge') }}</span>
            <span>{{ $t('devis.v3.locked_banner') }}</span>
        </div>

        <div class="ppv2-flex ppv2-flex-row content-layout">
            <div class="main-canvas ppv2-px-6 ppv2-py-4" :class="{ 'quote-locked': quoteLocked }" :title="lockTitle">
                <!-- Top Cards Row -->
                <div class="ppv2-flex lg:ppv2-flex-row ppv2-flex-col ppv2-gap-4 ppv2-justify-between ppv2-items-start ppv2-pb-6">
                    <div class="logo-column">
                        <!-- Logo Card -->
                        <div class="card-logo">
                            <CustomInputUpload
                                v-model="form.logo"
                                :showImproveWithAI="false"
                                accept="image/*"
                                :required="false"
                                :disabled="logoFromEstablishment || quoteLocked"
                            />
                        </div>
                        <div class="client-selector-box">
                            <!-- Recherche par saisie : la liste déroulante propose les fiches
                                 correspondantes et, en dernière position, la création. Le popup
                                 complet reste accessible juste en dessous — c'est lui qui porte
                                 le client de passage et le formulaire détaillé. -->
                            <ClientSearchSelect
                                v-if="!selectedClient || isPassageClient || changingClient"
                                ref="clientSearch"
                                :selected="isPassageClient || changingClient ? null : selectedClient"
                                @select="handleClientSelected"
                                @create="openClientCreation"
                            />

                            <!-- Client RÉEL rattaché : ses coordonnées et l'accès à sa fiche.
                                 Un client de passage n'a pas de fiche, il reste hors de cette
                                 branche comme auparavant. -->
                            <div
                                v-if="selectedClient && !isPassageClient && !changingClient"
                                class="client-summary ppv2-flex ppv2-w-fit ppv2-min-w-[330px] ppv2-max-w-[470px] ppv2-flex-col ppv2-gap-2 ppv2-rounded-[11px] ppv2-border-[1.5px] ppv2-border-solid ppv2-border-[#dbc1f0] ppv2-px-[14px] ppv2-py-3"
                                @click.stop
                            >
                                <div class="client-summary__row ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-[10px]">
                                    <div class="client-summary__copy ppv2-flex ppv2-min-w-0 ppv2-flex-col ppv2-gap-[3px]">
                                        <strong class="client-summary__name ppv2-whitespace-nowrap ppv2-text-sm poppinsSemiBold">
                                            {{
                                                selectedClient.display_name ||
                                                `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() ||
                                                selectedClient.email
                                            }}
                                            <span v-if="selectedClient.is_professional" class="client-summary__badge">Pro</span>
                                        </strong>
                                        <span
                                            class="client-summary__line ppv2-whitespace-nowrap ppv2-text-[12.5px] ppv2-text-[#6b7480]"
                                            >{{
                                                [selectedClient.email, selectedClient.phone_number || selectedClient.phone]
                                                    .filter(Boolean)
                                                    .join(' · ')
                                            }}</span
                                        >
                                        <span
                                            v-if="selectedClient.is_professional && selectedClient.siret"
                                            class="client-summary__line ppv2-text-[12.5px] ppv2-leading-[1.5] ppv2-text-[#444c57]"
                                            >SIREN {{ selectedClient.siret }}</span
                                        >
                                        <span
                                            v-if="selectedClient.address || selectedClient.city"
                                            class="client-summary__line ppv2-text-[12.5px] ppv2-leading-[1.5] ppv2-text-[#444c57]"
                                            >{{ selectedClient.address || selectedClient.city }}</span
                                        >
                                    </div>
                                    <div class="client-summary__actions ppv2-flex ppv2-shrink-0 ppv2-items-center ppv2-gap-[6px]">
                                        <button
                                            type="button"
                                            class="ppv2-flex ppv2-h-7 ppv2-w-7 ppv2-items-center ppv2-justify-center ppv2-rounded-lg ppv2-border-[1.5px] ppv2-border-solid ppv2-border-[#dbc1f0] ppv2-bg-white dark:ppv2-bg-dark ppv2-text-[#7d2dbd]"
                                            :title="$t('devisV3.clientSearch.change')"
                                            @click="changeClient"
                                        >
                                            <RotateCcw :size="13" />
                                        </button>
                                        <button
                                            type="button"
                                            class="ppv2-flex ppv2-h-7 ppv2-w-7 ppv2-items-center ppv2-justify-center ppv2-rounded-lg ppv2-border-[1.5px] ppv2-border-solid ppv2-border-[#dbc1f0] ppv2-bg-white dark:ppv2-bg-dark ppv2-text-[#7d2dbd]"
                                            :title="$t('devisV3.clientSearch.edit')"
                                            @click="openClientEdition"
                                        >
                                            <Pencil :size="12" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Draggable Post-it-->
                    <!-- Demande client : champ propre (orders.client_request). Il partageait
                         `note` avec le champ de notes du bas de page, si bien que les deux
                         s'écrasaient mutuellement. -->
                    <!-- Devis accepté : la demande client passe en lecture seule (readonly +
                         tooltip de verrou). -->
                    <DraggablePostIt
                        ref="postIt"
                        v-model="clientRequest"
                        :visible.sync="postItVisible"
                        :readonly="quoteLocked"
                        :title="lockTitle"
                    />

                    <!-- Quote Info & Client -->
                    <div class="card-quote-info">
                        <div class="ppv2-mb-2">
                            <h2 class="poppinsSemiBold !ppv2-mb-0" :style="{ fontSize: normalizeFontSize(30) }">
                                {{ $t('devis.v3.quote_label') }}
                            </h2>
                            <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(30) }">#2025-183</span>
                        </div>
                        <div class="devis-dates">
                            <div class="date-row">
                                <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                    $t('devis.v3.issue_date')
                                }}</span>
                                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">{{
                                    formatDate(issueDate)
                                }}</span>
                            </div>
                            <div class="date-row">
                                <span class="poppinsMedium" :style="{ fontSize: normalizeFontSize(15) }">{{
                                    $t('devis.v3.due_date')
                                }}</span>
                                <!-- Échéance CALCULÉE : date d'émission + durée de validité (panneau Options).
                                     Plus de calendrier ici : la seule façon de la déplacer est de changer la
                                     durée de validité, ce qui garantit qu'elle reste cohérente avec les dates
                                     des lignes (contrôle en deux sens, front + serveur). -->
                                <span
                                    class="poppinsRegular ppv2-text-black"
                                    :style="{ fontSize: normalizeFontSize(15) }"
                                    :title="$t('devis.v3.due_date_computed_hint')"
                                >
                                    {{ formatDate(form.due_date) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Lines Table -->
                <div
                    class="ppv2-p-5 ppv2-bg-white dark:ppv2-bg-dark ppv2-border ppv2-border-solid ppv2-border-black ppv2-border-opacity-10 ppv2-rounded-xl"
                >
                    <!-- Bascule d'affichage HT / TTC : purement visuelle. Les colonnes PU et Total
                         changent de base (libellé + montant) ; aucune donnée n'est modifiée ni renvoyée
                         au serveur (la remise, la TVA et les totaux stockés restent inchangés). -->
                    <div class="ppv2-flex ppv2-justify-end ppv2-items-center ppv2-mb-3">
                        <div
                            class="price-display"
                            v-click-outside="closePriceDisplayMenu"
                            @keydown.esc.stop.prevent="selectPriceDisplayMode(priceDisplayMode)"
                        >
                            <button
                                type="button"
                                class="price-display__trigger poppinsMedium"
                                ref="priceDisplayTrigger"
                                :aria-expanded="priceDisplayMenuOpen ? 'true' : 'false'"
                                aria-haspopup="menu"
                                :title="$t('devis.v3.price_display_title')"
                                @click="togglePriceDisplayMenu"
                            >
                                <span>
                                    {{ $t('devis.v3.price_display_label') }}
                                    {{
                                        $t(priceDisplayMode === 'ht' ? 'devis.v3.price_display_ht' : 'devis.v3.price_display_ttc')
                                    }}
                                </span>
                                <ChevronDown :size="9" class="price-display__chevron" />
                            </button>
                            <div v-if="priceDisplayMenuOpen" class="price-display__menu" role="menu">
                                <button
                                    type="button"
                                    role="menuitemradio"
                                    class="price-display__option"
                                    :class="{ 'is-active': priceDisplayMode === 'ht' }"
                                    :aria-checked="priceDisplayMode === 'ht' ? 'true' : 'false'"
                                    @click="selectPriceDisplayMode('ht')"
                                >
                                    <span>{{ $t('devis.v3.price_display_option_ht') }}</span>
                                    <Check v-if="priceDisplayMode === 'ht'" :size="12" />
                                </button>
                                <button
                                    type="button"
                                    role="menuitemradio"
                                    class="price-display__option"
                                    :class="{ 'is-active': priceDisplayMode === 'ttc' }"
                                    :aria-checked="priceDisplayMode === 'ttc' ? 'true' : 'false'"
                                    @click="selectPriceDisplayMode('ttc')"
                                >
                                    <span>{{ $t('devis.v3.price_display_option_ttc') }}</span>
                                    <Check v-if="priceDisplayMode === 'ttc'" :size="12" />
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="table-responsive-wrapper">
                        <table class="devis-table">
                            <thead>
                                <tr>
                                    <th class="col-grip md:ppv2-p-4 ppv2-p-2 ppv2-w-10"></th>
                                    <th class="col-desc md:ppv2-p-4 ppv2-p-2">{{ $t('devis.v3.designation') }}</th>
                                    <th class="col-optional md:ppv2-p-4 ppv2-p-2">{{ $t('devis.v3.optional_line') }}</th>
                                    <th class="col-qty md:ppv2-p-4 ppv2-p-2">{{ $t('devis.v3.qty') }}</th>
                                    <th class="col-price md:ppv2-p-4 ppv2-p-2">
                                        {{
                                            priceDisplayMode === 'ht'
                                                ? $t('devis.v3.unit_price_ht')
                                                : $t('devis.v3.unit_price_ttc')
                                        }}
                                    </th>
                                    <th class="col-discount md:ppv2-p-4 ppv2-p-2">{{ $t('devis.v3.discount') }}</th>
                                    <th class="col-tva md:ppv2-p-4 ppv2-p-2">{{ $t('devis.v3.tva') }}</th>
                                    <th class="col-total md:ppv2-p-4 ppv2-p-2">
                                        {{ priceDisplayMode === 'ht' ? $t('devis.v3.total_ht') : $t('devis.v3.total_ttc') }}
                                    </th>
                                    <th class="col-actions md:ppv2-p-4 ppv2-p-2"></th>
                                </tr>
                            </thead>
                            <!--
                                Un <tbody> = une ligne du devis. SEULE une prestation simpleTicket
                                (drapeau serveur `is_container`) est VERROUILLÉE : elle est enregistrée
                                à 0 € / 0 % de TVA car ses billets portent tout le montant. Elle est donc
                                sans montant et non modifiable, et le groupe se déplace d'un bloc
                                (billets et conteneur partagent booking et créneau).

                                Une ligne RESTO regroupe elle aussi des enfants (options, suppléments,
                                produits d'un menu) mais elle porte SON PROPRE prix : le verrou se
                                décide donc sur `is_container`, jamais sur la présence d'enfants —
                                sinon un plat avec supplément s'afficherait sans montant.

                                Toute autre ligne — dont une option produit rattachée à une prestation
                                payante — est rendue à l'identique de l'existant : montants, poignée de
                                déplacement et corbeille.
                            -->
                            <tbody v-for="group in itemGroups" :key="'group-' + group.parent.id" class="item-group">
                                <tr
                                    :class="{
                                        'is-container': group.parent.is_container,
                                        'is-optional': group.parent.is_optional,
                                    }"
                                >
                                    <td class="col-grip ppv2-w-10" :class="itemGroups.length > 1 ? 'ppv2-cursor-grab' : ''">
                                        <GripVertical v-if="itemGroups.length > 1" :size="16" class="gripHandle" />
                                    </td>
                                    <td class="col-desc">
                                        <div class="item-desc">
                                            <strong class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                <Lock
                                                    v-if="group.parent.is_container"
                                                    :size="12"
                                                    class="containerLock"
                                                    :title="$t('devis.v3.container_line')"
                                                />
                                                <template v-if="editingItemId === group.parent.id">
                                                    <!-- Capture : seul le champ nom est affiché,
                                                         validation auto sur Entrée / blur / Escape. -->
                                                    <input
                                                        v-if="customLineCaptureId === group.parent.id"
                                                        ref="captureInput"
                                                        type="text"
                                                        v-model="editForm.custom_label"
                                                        :placeholder="$t('devis.v3.custom_line_default')"
                                                        @keyup.enter="autoSaveCapture()"
                                                        @blur="autoSaveCapture()"
                                                        @keyup.escape="cancelCapture()"
                                                        style="border: 1px solid #7d2dbd !important"
                                                        class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-full ppv2-outline-none"
                                                    />
                                                    <input
                                                        v-else
                                                        type="text"
                                                        v-model="editForm.custom_label"
                                                        style="border: 1px solid #7d2dbd !important"
                                                        class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-full ppv2-outline-none"
                                                    />
                                                </template>
                                                <template v-else-if="isCellEditing(group.parent, 'custom_label')">
                                                    <input
                                                        :ref="'cellEditInput_' + group.parent.id"
                                                        type="text"
                                                        v-model="cellEdit.value"
                                                        :placeholder="$t('devis.v3.custom_line_default')"
                                                        @keyup.enter="saveCellEdit()"
                                                        @blur="saveCellEdit()"
                                                        @keyup.escape="cancelCellEdit()"
                                                        style="border: 1px solid #7d2dbd !important"
                                                        class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-full ppv2-outline-none"
                                                    />
                                                </template>
                                                <template v-else>
                                                    <span class="cell-with-pencil item-name-cell">
                                                        <span
                                                            class="item-name-value"
                                                            :title="
                                                                group.parent.more_details?.custom_label ||
                                                                (group.parent.itemable
                                                                    ? group.parent.itemable.name
                                                                    : $t('devis.v3.unnamed_article'))
                                                            "
                                                            >{{
                                                                group.parent.more_details?.custom_label ||
                                                                (group.parent.itemable
                                                                    ? group.parent.itemable.name
                                                                    : $t('devis.v3.unnamed_article'))
                                                            }}</span
                                                        >
                                                        <button
                                                            v-if="isEditableLine(group.parent) && !quoteLocked"
                                                            type="button"
                                                            class="cell-edit-pencil"
                                                            :title="$t('devis.v3.edit')"
                                                            @click.stop="startCellEdit(group.parent, 'custom_label')"
                                                        >
                                                            <Pencil :size="12" />
                                                        </button>
                                                    </span>
                                                </template>
                                                <!-- Repère « espace entier » (cf. isWholeSpaceBooking) : explicite les
                                                     participants et le prix PAR PLACE, pour que le PU de groupe affiché
                                                     sur une quantité 1 ne soit pas pris pour un bug. Informatif, ne
                                                     touche ni le PU ni le total. Visible UNIQUEMENT pour ce cas. -->
                                                <span
                                                    v-if="isWholeSpaceBooking(group.parent)"
                                                    class="ppv2-block ppv2-mt-0.5 ppv2-text-xs ppv2-text-[#6b7280] poppinsRegular"
                                                    >{{
                                                        $t('devis.v3.whole_space_hint', {
                                                            count: group.parent.nb_contributor,
                                                            unit: formatCurrency(perPlaceUnitPrice(group.parent)),
                                                        })
                                                    }}</span
                                                >
                                                <button
                                                    v-if="noteEditingId !== group.parent.id && !group.parent.comment"
                                                    type="button"
                                                    class="item-note-link"
                                                    :title="$t('devis.v3.add_note')"
                                                    :aria-label="$t('devis.v3.add_note')"
                                                    :disabled="savingNote || quoteLocked"
                                                    @click.stop="startItemNote(group.parent)"
                                                >
                                                    <Plus :size="10" />
                                                    <span>Note</span>
                                                </button>
                                                <!-- « + option » : ajoute une ligne enfant sous la ligne
                                                     PRODUIT (ou menu resto). Réservé à ce qui peut porter des
                                                     options — pas une prestation, pas un conteneur simpleTicket
                                                     (miroir serveur DevisProductItemService::addCustomLine).
                                                     Révélée au survol avec « + Note » (cf. .item-note-link) ;
                                                     la ligne « Ajouter une option » du groupe reste le second
                                                     point d'entrée dès qu'une option existe déjà. -->
                                                <button
                                                    v-if="canAddOption(group.parent)"
                                                    type="button"
                                                    class="item-note-link"
                                                    :title="$t('devis.v3.add_option')"
                                                    :aria-label="$t('devis.v3.add_option')"
                                                    :disabled="
                                                        addingCustomLine ||
                                                        savingNote ||
                                                        editingItemId !== null ||
                                                        cellEdit.saving
                                                    "
                                                    @click.stop="addOption(group.parent)"
                                                >
                                                    <Plus :size="10" />
                                                    <span>{{ $t('devis.v3.option') }}</span>
                                                </button>
                                            </strong>
                                            <!-- En édition, la date porte la même bordure que le libellé et ouvre
                                                 la popup de planning sur le créneau courant. Disponible dans les deux
                                                 modes : avec créneau la réservation est déplacée, sans créneau seule
                                                 la date indicative portée par les lignes change. -->
                                            <button
                                                v-if="group.parent.start_time && editingItemId === group.parent.id"
                                                type="button"
                                                style="border: 1px solid #7d2dbd !important"
                                                class="date ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-primary ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-mt-1 ppv2-text-left ppv2-outline-none hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-all"
                                                @click="openSlotEditor(group.parent)"
                                            >
                                                <span>
                                                    {{ $t('devis.v3.date_prefix') }} {{ formatDate(group.parent.start_time) }} à
                                                    {{ group.parent.start_time.substring(11, 16) }}
                                                </span>
                                                <Pencil :size="12" />
                                            </button>
                                            <span v-else-if="group.parent.start_time" class="date ppv2-flex ppv2-flex-col">
                                                {{ $t('devis.v3.date_prefix') }} {{ formatDate(group.parent.start_time) }} à
                                                {{ group.parent.start_time.substring(11, 16) }}
                                            </span>

                                            <!-- Note libre, directement sous la désignation et dans la même
                                                 cellule : elle appartient à la ligne, pas à une ligne à part. -->
                                            <div class="item-note ppv2-mt-1">
                                                <template v-if="noteEditingId === group.parent.id">
                                                    <div class="item-note-editor ppv2-flex ppv2-items-start ppv2-gap-2">
                                                        <textarea
                                                            v-model="noteDraft"
                                                            :rows="Math.min(5, (noteDraft || '').split('\n').length)"
                                                            :placeholder="$t('devis.v3.item_note_placeholder')"
                                                            class="item-note-input ppv2-flex-1"
                                                            @blur="handleItemNoteBlur($event, group.parent)"
                                                            @keydown="handleItemNoteKeydown($event, group.parent)"
                                                        ></textarea>
                                                    </div>
                                                </template>
                                                <span
                                                    v-else-if="group.parent.comment"
                                                    class="item-note-text ppv2-d-inline-flex ppv2-items-center ppv2-gap-1 ppv2-text-[#6b7480] ppv2-text-xs ppv2-px-[2px] ppv2-py-[6px] hover:ppv2-decoration-underline hover:ppv2-decoration-dotted hover:ppv2-text-primary"
                                                    role="button"
                                                    tabindex="0"
                                                    :title="group.parent.comment"
                                                    :aria-label="$t('devis.v3.edit')"
                                                    @click.stop="startItemNote(group.parent)"
                                                    @keydown.enter.stop.prevent="startItemNote(group.parent)"
                                                    @keydown.space.stop.prevent="startItemNote(group.parent)"
                                                >
                                                    {{ group.parent.comment }}
                                                    <Pencil :size="11" class="item-note-pencil" />
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <!-- Colonne « Option » : une ligne cochée est PROPOSÉE au client mais
                                         EXCLUE du total. Le serveur recalcule ; rien n'est recompté ici.
                                         @mousedown protège le focus d'une édition en cours (son blur
                                         validerait ou supprimerait un brouillon). -->
                                    <td
                                        class="col-optional"
                                        @mousedown="onOptionCellMousedown"
                                        :title="quoteLocked && !group.parent.is_optional ? lockTitle : null"
                                    >
                                        <input
                                            v-if="editingItemId === group.parent.id"
                                            type="checkbox"
                                            class="option-checkbox"
                                            v-model="editForm.is_optional"
                                            :title="$t('devis.v3.optional_line')"
                                        />
                                        <input
                                            v-else
                                            type="checkbox"
                                            class="option-checkbox"
                                            :checked="group.parent.is_optional"
                                            :disabled="
                                                optionUpdatingId !== null ||
                                                cellEdit.saving ||
                                                (quoteLocked && !group.parent.is_optional)
                                            "
                                            :title="
                                                quoteLocked && !group.parent.is_optional
                                                    ? lockTitle
                                                    : $t('devis.v3.optional_line')
                                            "
                                            @change="toggleOptional(group.parent, $event)"
                                        />
                                    </td>
                                    <td class="col-qty">
                                        <template v-if="editingItemId === group.parent.id">
                                            <!-- Capture : la ligne est un brouillon (nom vide, qté 1, prix 0). La maquette garde le
                                                 stepper PLEINEMENT ACTIF pendant la saisie du nom —
                                                 `qtyCell` est une colonne sœur de `editingName` dans
                                                 « Devis - Lignes produits ». `hold-focus` empêche
                                                 surtout que le clic sur « + » ne fasse perdre le focus
                                                 de l'input : son `blur` déclencherait
                                                 `autoSaveCapture`, qui SUPPRIMERait le brouillon si le
                                                 nom est encore vide. -->
                                            <QtyStepper
                                                v-if="customLineCaptureId === group.parent.id"
                                                :quantity="group.parent.quantity"
                                                size="lg"
                                                :editing="isCellEditing(group.parent, 'quantity')"
                                                :draft="cellEdit.value"
                                                :disabled="!canTouchQty(group.parent)"
                                                hold-focus
                                                :remove-title="$t('devis.v3.remove_line')"
                                                @step="stepQuantity(group.parent, $event)"
                                                @edit-start="startCellEdit(group.parent, 'quantity')"
                                                @remove="onQtyRemove(group.parent)"
                                                @input="setQtyCellDraft($event)"
                                                @commit="saveCellEdit"
                                                @cancel="cancelCellEdit"
                                            />
                                            <!-- Avec créneau : même sélecteur que l'étape Participants du
                                                 stepper — valeurs issues du catalogue (possible_tickets /
                                                 min / max_contributors), capacité réelle contrôlée côté
                                                 serveur. `v-else-if` et non `v-if` : la chaîne doit
                                                 rester LINÉAIRE, sinon le <span> du dessous — qui
                                                 s'accroche au `v-if` du BaseSelect — s'affiche en
                                                 plus du stepper de capture (deux contrôles dans la
                                                 même cellule). -->
                                            <BaseSelect
                                                v-else-if="
                                                    !group.parent.is_container && usesTimeSlots && isPrestationLine(group.parent)
                                                "
                                                v-model="editForm.quantity"
                                                :options="editQuantityOptions"
                                                :clearable="false"
                                                :searchable="false"
                                                :disabled="loadingEditOptions"
                                                class="ppv2-w-40"
                                                customInputClass="!ppv2-opacity-100"
                                            />
                                            <!-- Sans créneau : aucune capacité n'est consommée, la quantité se règle au stepper
                                                 de la colonne Qté (boutons − / +, nombre cliquable). On
                                                 n'édite donc PAS la quantité dans ce formulaire : on la
                                                 lit, et on la renvoie telle quelle à saveEditing.
                                                 Le conteneur reste en lecture simple (quantité = somme
                                                 des billets). -->
                                            <span
                                                v-else-if="!group.parent.is_container"
                                                class="poppinsMedium ppv2-text-[#1d1d1b]"
                                                >{{ group.parent.quantity }}</span
                                            >
                                            <span v-else>{{ group.parent.quantity }}</span>
                                        </template>
                                        <template v-else>
                                            <!-- Stepper direct : − / qté / + sans passer par le mode édition.
                                                 Le nombre est lui-même cliquable (QtyStepper) et devient alors
                                                 un input SANS flèches : les seuls réglages restent les boutons,
                                                 la saisie directe n'est qu'un raccourci.
                                                 À qté = 1, le « − » devient une corbeille (suppression). Réservé
                                                 aux lignes à quantité libre (produits, lignes personnalisées, ventes
                                                 additionnelles) ; conteneurs et prestations à créneau gardent leur
                                                 affichage. -->
                                            <QtyStepper
                                                v-if="hasQtyStepper(group.parent)"
                                                :quantity="group.parent.quantity"
                                                size="lg"
                                                :editing="isCellEditing(group.parent, 'quantity')"
                                                :draft="cellEdit.value"
                                                :disabled="!canTouchQty(group.parent)"
                                                :remove-title="$t('devis.v3.remove_line')"
                                                @step="stepQuantity(group.parent, $event)"
                                                @edit-start="startCellEdit(group.parent, 'quantity')"
                                                @remove="onQtyRemove(group.parent)"
                                                @input="setQtyCellDraft($event)"
                                                @commit="saveCellEdit"
                                                @cancel="cancelCellEdit"
                                            />
                                            <span v-else>{{ group.parent.quantity }}</span>
                                        </template>
                                    </td>
                                    <!-- Conteneur : PU HT / remise / TVA / total muets (tout est sur les billets) -->
                                    <td class="col-price">
                                        <template
                                            v-if="
                                                editingItemId === group.parent.id &&
                                                !group.parent.is_container &&
                                                customLineCaptureId !== group.parent.id
                                            "
                                        >
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                <input
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    v-model.number="editForm.price_ht"
                                                    style="border: 1px solid #7d2dbd !important"
                                                    class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-28 ppv2-text-right ppv2-outline-none"
                                                />
                                                <span class="poppinsMedium">€</span>
                                            </div>
                                        </template>
                                        <template v-else-if="isCellEditing(group.parent, 'price_ht')">
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                <input
                                                    :ref="'cellEditInput_' + group.parent.id"
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    v-model.number="cellEdit.value"
                                                    @keyup.enter="saveCellEdit()"
                                                    @blur="saveCellEdit()"
                                                    @keyup.escape="cancelCellEdit()"
                                                    style="border: 1px solid #7d2dbd !important"
                                                    class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-28 ppv2-text-right ppv2-outline-none"
                                                />
                                                <span class="poppinsMedium">€</span>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <span v-if="group.parent.is_container"></span>
                                            <span v-else class="cell-with-pencil">
                                                {{
                                                    formatCurrency(
                                                        priceDisplayMode === 'ht' ? group.parent.price_ht : group.parent.price
                                                    )
                                                }}
                                                <!-- Crayon au survol : édite le P.U. HT seul (base de calcul). -->
                                                <button
                                                    v-if="isEditableLine(group.parent) && !quoteLocked"
                                                    type="button"
                                                    class="cell-edit-pencil"
                                                    :title="$t('devis.v3.edit')"
                                                    @click.stop="startCellEdit(group.parent, 'price_ht')"
                                                >
                                                    <Pencil :size="12" />
                                                </button>
                                            </span>
                                        </template>
                                    </td>
                                    <td class="col-discount">
                                        <template v-if="isCellEditing(group.parent, 'remise_percent')">
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-1 ppv2-justify-center">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                    :ref="'cellEditInput_' + group.parent.id"
                                                    v-model.number="cellEdit.value"
                                                    @keyup.enter="saveCellEdit()"
                                                    @blur="saveCellEdit()"
                                                    @keyup.escape="cancelCellEdit()"
                                                    style="border: 1px solid #7d2dbd !important"
                                                    class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-2 ppv2-py-2 ppv2-w-16 ppv2-text-center ppv2-outline-none"
                                                />
                                                <span class="poppinsMedium">%</span>
                                            </div>
                                        </template>
                                        <span v-else-if="group.parent.is_container"></span>
                                        <span v-else class="cell-with-pencil">
                                            {{ group.parent.remise_percent || 0 }}%
                                            <button
                                                v-if="isEditableLine(group.parent) && !quoteLocked"
                                                class="cell-edit-pencil"
                                                :title="$t('devis.v3.edit')"
                                                @click.stop="startCellEdit(group.parent, 'remise_percent')"
                                            >
                                                <Pencil :size="12" />
                                            </button>
                                        </span>
                                    </td>
                                    <td class="col-tva" :class="{ 'vat-cell-editing': isCellEditing(group.parent, 'vat_rate') }">
                                        <template v-if="isCellEditing(group.parent, 'vat_rate')">
                                            <!-- Menu porté par BaseSelect en `fixed-dropdown` : ce mode
                                                 envoie le menu dans <body> et le positionne en fixed
                                                 d'après le rect du champ (applyFixedDropdownPosition).
                                                 L'ancien .vat-select, en absolute à l'intérieur du
                                                 tableau, se faisait rogner par .table-responsive-wrapper
                                                 et .main-canvas — son div existait mais restait
                                                 invisible. BaseSelect apporte aussi l'ouverture /
                                                 fermeture, le click-outside et la touche Échap. -->
                                            <div class="vat-cell-edit" @click.stop>
                                                <BaseSelect
                                                    :ref="'vatCellSelect_' + group.parent.id"
                                                    :value="cellEdit.value"
                                                    :options="vatRateOptions(group.parent)"
                                                    :clearable="false"
                                                    :searchable="false"
                                                    :disabled="cellEdit.saving"
                                                    fixed-dropdown
                                                    auto-flip
                                                    compact
                                                    class="ppv2-w-20"
                                                    customInputClass="!ppv2-opacity-100"
                                                    @input="selectVatRate($event)"
                                                />
                                            </div>
                                        </template>
                                        <span v-else-if="group.parent.is_container"></span>
                                        <span
                                            v-else
                                            class="cell-with-pencil"
                                            :class="{ 'vat-rate-editable': canEditVat(group.parent) }"
                                            @click.stop="canEditVat(group.parent) && startCellEdit(group.parent, 'vat_rate')"
                                        >
                                            {{ group.parent.vat_rate }}%
                                            <button
                                                v-if="canEditVat(group.parent)"
                                                class="cell-edit-pencil"
                                                :title="$t('devis.v3.edit')"
                                                @click.stop="startCellEdit(group.parent, 'vat_rate')"
                                            >
                                                <Pencil :size="12" />
                                            </button>
                                        </span>
                                    </td>
                                    <td class="col-total">
                                        <div class="col-total-action">
                                            <span v-if="!group.parent.is_container">
                                                <template v-if="isCellEditing(group.parent, 'total')">
                                                    <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                        <input
                                                            :ref="'cellEditInput_' + group.parent.id"
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            v-model.number="cellEdit.value"
                                                            @keyup.enter="saveCellEdit()"
                                                            @blur="saveCellEdit()"
                                                            @keyup.escape="cancelCellEdit()"
                                                            style="border: 1px solid #7d2dbd !important"
                                                            class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-2 ppv2-py-2 ppv2-w-28 ppv2-text-right ppv2-outline-none"
                                                        />
                                                        <span class="poppinsMedium">€</span>
                                                    </div>
                                                </template>
                                                <template v-else-if="editingItemId === group.parent.id">
                                                    {{
                                                        formatCurrency(
                                                            priceDisplayMode === 'ht'
                                                                ? editForm.quantity *
                                                                      editForm.price_ht *
                                                                      (1 - (editForm.remise_percent || 0) / 100)
                                                                : editForm.quantity *
                                                                      editForm.price_ht *
                                                                      (1 + editForm.vat_rate / 100)
                                                        )
                                                    }}
                                                </template>
                                                <template v-else>
                                                    <span class="cell-with-pencil">
                                                        {{
                                                            formatCurrency(
                                                                priceDisplayMode === 'ht'
                                                                    ? lineTotalHt(group.parent)
                                                                    : lineTotal(group.parent)
                                                            )
                                                        }}
                                                        <button
                                                            v-if="canEditTotal(group.parent)"
                                                            type="button"
                                                            class="cell-edit-pencil"
                                                            :title="$t('devis.v3.edit_total')"
                                                            @click.stop="startCellEdit(group.parent, 'total')"
                                                        >
                                                            <Pencil :size="12" />
                                                        </button>
                                                    </span>
                                                </template>
                                            </span>
                                            <span v-else></span>
                                        </div>
                                    </td>
                                    <td class="col-actions">
                                        <div class="row-actions">
                                            <template v-if="editingItemId === group.parent.id">
                                                <template v-if="customLineCaptureId !== group.parent.id">
                                                    <button
                                                        class="ppv2-p-1 ppv2-text-green-600 hover:ppv2-bg-green-100 ppv2-rounded ppv2-transition-all"
                                                        @click="saveEditing()"
                                                        :disabled="savingEdit"
                                                    >
                                                        <Check :size="16" />
                                                    </button>
                                                    <button
                                                        class="ppv2-p-1 ppv2-text-red-500 hover:ppv2-bg-red-100 ppv2-rounded ppv2-transition-all"
                                                        @click="cancelEditing()"
                                                        :disabled="savingEdit"
                                                    >
                                                        <X :size="16" />
                                                    </button>
                                                </template>
                                            </template>
                                            <template v-else>
                                                <button
                                                    class="ppv2-p-1 ppv2-text-black ppv2-opacity-70 hover:ppv2-text-opacity-50 hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                                    :disabled="
                                                        duplicatingItemId !== null || editingItemId !== null || quoteLocked
                                                    "
                                                    @click="handleDuplicateItem(group.parent.id)"
                                                >
                                                    <CopyPlus :size="14" />
                                                </button>
                                                <button
                                                    v-if="!group.parent.is_container"
                                                    class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                                    :disabled="editingItemId !== null || quoteLocked"
                                                    @click="handleRemoveItem(group.parent.id)"
                                                >
                                                    <Trash2 :size="14" />
                                                </button>
                                            </template>
                                        </div>
                                    </td>
                                </tr>
                                <!-- Lignes incluses : elles portent le montant réel et sont supprimables -->
                                <tr
                                    v-for="child in group.children"
                                    :key="'item-' + child.id"
                                    class="is-child"
                                    :class="{ 'is-optional': group.parent.is_optional }"
                                >
                                    <td class="col-grip ppv2-w-10"></td>
                                    <td class="col-desc">
                                        <div class="item-desc child-desc">
                                            <span class="child-bullet">•</span>
                                            <!-- Option produit : désignation éditable par crayon de cellule. Les
                                                 autres enfants (billets, suppléments) restent en lecture seule. -->
                                            <template v-if="isCellEditing(child, 'custom_label')">
                                                <input
                                                    type="text"
                                                    :ref="'cellEditInput_' + child.id"
                                                    v-model="cellEdit.value"
                                                    @keyup.enter="saveCellEdit()"
                                                    @blur="saveCellEdit()"
                                                    @keyup.escape="cancelCellEdit()"
                                                    style="border: 1px solid #7d2dbd !important"
                                                    class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-full ppv2-text-sm ppv2-outline-none"
                                                />
                                            </template>
                                            <span v-else class="cell-with-pencil item-name-cell">
                                                <span
                                                    class="item-name-value"
                                                    :title="
                                                        child.more_details?.custom_label ||
                                                        (child.itemable ? child.itemable.name : $t('devis.v3.unnamed_article'))
                                                    "
                                                    >{{
                                                        child.more_details?.custom_label ||
                                                        (child.itemable ? child.itemable.name : $t('devis.v3.unnamed_article'))
                                                    }}</span
                                                >
                                                <button
                                                    v-if="isEditableChild(child, group) && !quoteLocked"
                                                    class="cell-edit-pencil"
                                                    :title="$t('devis.v3.edit')"
                                                    @click.stop="startCellEdit(child, 'custom_label')"
                                                >
                                                    <Pencil :size="12" />
                                                </button>
                                            </span>
                                        </div>
                                    </td>
                                    <td class="col-optional"></td>
                                    <td class="col-qty">
                                        <!-- Option / supplément : même contrôle que la ligne parente, en plus petit.
                                             Le serveur autorise sa quantité (cf. updateItemInQuote) ; la corbeille
                                             passe par handleRemoveItem(child, group), qui protège déjà le
                                             « dernier article » quand le parent est un conteneur. -->
                                        <QtyStepper
                                            v-if="hasChildQtyStepper(child, group)"
                                            :quantity="child.quantity"
                                            size="sm"
                                            :editing="isCellEditing(child, 'quantity')"
                                            :draft="cellEdit.value"
                                            :disabled="!canTouchQty(child)"
                                            :remove-title="$t('devis.v3.remove_option')"
                                            @step="stepQuantity(child, $event)"
                                            @edit-start="startCellEdit(child, 'quantity')"
                                            @remove="onQtyRemove(child, group)"
                                            @input="setQtyCellDraft($event)"
                                            @commit="saveCellEdit"
                                            @cancel="cancelCellEdit"
                                        />
                                        <span v-else>{{ child.quantity }}</span>
                                    </td>
                                    <td class="col-price">
                                        <template v-if="isCellEditing(child, 'price_ht')">
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                <input
                                                    type="number"
                                                    step="1"
                                                    min="0"
                                                    :ref="'cellEditInput_' + child.id"
                                                    v-model.number="cellEdit.value"
                                                    @keyup.enter="saveCellEdit()"
                                                    @blur="saveCellEdit()"
                                                    @keyup.escape="cancelCellEdit()"
                                                    style="border: 1px solid #7d2dbd !important"
                                                    class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-28 ppv2-text-right ppv2-outline-none"
                                                />
                                                <span class="poppinsMedium">€</span>
                                            </div>
                                        </template>
                                        <span v-else class="cell-with-pencil">
                                            {{ formatCurrency(priceDisplayMode === 'ht' ? child.price_ht : child.price) }}
                                            <button
                                                v-if="isEditableChild(child, group)"
                                                class="cell-edit-pencil"
                                                :title="$t('devis.v3.edit')"
                                                @click.stop="startCellEdit(child, 'price_ht')"
                                            >
                                                <Pencil :size="12" />
                                            </button>
                                        </span>
                                    </td>
                                    <td class="col-discount">
                                        <template v-if="isCellEditing(child, 'remise_percent')">
                                            <div class="ppv2-flex ppv2-items-center ppv2-gap-1 ppv2-justify-center">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                    :ref="'cellEditInput_' + child.id"
                                                    v-model.number="cellEdit.value"
                                                    @keyup.enter="saveCellEdit()"
                                                    @blur="saveCellEdit()"
                                                    @keyup.escape="cancelCellEdit()"
                                                    style="border: 1px solid #7d2dbd !important"
                                                    class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-2 ppv2-py-2 ppv2-w-16 ppv2-text-center ppv2-outline-none"
                                                />
                                                <span class="poppinsMedium">%</span>
                                            </div>
                                        </template>
                                        <span v-else class="cell-with-pencil">
                                            {{ child.remise_percent || 0 }}%
                                            <button
                                                v-if="isEditableChild(child, group)"
                                                class="cell-edit-pencil"
                                                :title="$t('devis.v3.edit')"
                                                @click.stop="startCellEdit(child, 'remise_percent')"
                                            >
                                                <Pencil :size="12" />
                                            </button>
                                        </span>
                                    </td>
                                    <td class="col-tva" :class="{ 'vat-cell-editing': isCellEditing(child, 'vat_rate') }">
                                        <template v-if="isCellEditing(child, 'vat_rate')">
                                            <!-- Même sélecteur que sur la ligne principale (cf. dessus) :
                                                 même composant, même mode fixed-dropdown. -->
                                            <div class="vat-cell-edit" @click.stop>
                                                <BaseSelect
                                                    :ref="'vatCellSelect_' + child.id"
                                                    :value="cellEdit.value"
                                                    :options="vatRateOptions(child)"
                                                    :clearable="false"
                                                    :searchable="false"
                                                    :disabled="cellEdit.saving"
                                                    fixed-dropdown
                                                    auto-flip
                                                    compact
                                                    class="ppv2-w-20"
                                                    customInputClass="!ppv2-opacity-100"
                                                    @input="selectVatRate($event)"
                                                />
                                            </div>
                                        </template>
                                        <span
                                            v-else
                                            class="cell-with-pencil"
                                            :class="{ 'vat-rate-editable': canEditVat(child) }"
                                            @click.stop="canEditVat(child) && startCellEdit(child, 'vat_rate')"
                                        >
                                            {{ child.vat_rate }}%
                                            <button
                                                v-if="canEditVat(child)"
                                                class="cell-edit-pencil"
                                                :title="$t('devis.v3.edit')"
                                                @click.stop="startCellEdit(child, 'vat_rate')"
                                            >
                                                <Pencil :size="12" />
                                            </button>
                                        </span>
                                    </td>
                                    <td class="col-total">
                                        <div class="col-total-action">
                                            <template v-if="isCellEditing(child, 'total')">
                                                <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                                    <input
                                                        :ref="'cellEditInput_' + child.id"
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        v-model.number="cellEdit.value"
                                                        @keyup.enter="saveCellEdit()"
                                                        @blur="saveCellEdit()"
                                                        @keyup.escape="cancelCellEdit()"
                                                        style="border: 1px solid #7d2dbd !important"
                                                        class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-2 ppv2-py-2 ppv2-w-28 ppv2-text-right ppv2-outline-none"
                                                    />
                                                    <span class="poppinsMedium">€</span>
                                                </div>
                                            </template>
                                            <template v-else-if="editingItemId === child.id">
                                                <span>{{
                                                    formatCurrency(
                                                        priceDisplayMode === 'ht'
                                                            ? editForm.quantity *
                                                                  editForm.price_ht *
                                                                  (1 - (editForm.remise_percent || 0) / 100)
                                                            : editForm.quantity *
                                                                  editForm.price_ht *
                                                                  (1 + editForm.vat_rate / 100)
                                                    )
                                                }}</span>
                                            </template>
                                            <template v-else>
                                                <span class="cell-with-pencil">
                                                    {{
                                                        formatCurrency(
                                                            priceDisplayMode === 'ht' ? lineTotalHt(child) : lineTotal(child)
                                                        )
                                                    }}
                                                    <button
                                                        v-if="canEditTotal(child, group)"
                                                        type="button"
                                                        class="cell-edit-pencil"
                                                        :title="$t('devis.v3.edit_total')"
                                                        @click.stop="startCellEdit(child, 'total')"
                                                    >
                                                        <Pencil :size="12" />
                                                    </button>
                                                </span>
                                            </template>
                                        </div>
                                    </td>
                                    <td class="col-actions">
                                        <div class="row-actions">
                                            <template v-if="editingItemId === child.id">
                                                <button
                                                    class="ppv2-p-1 ppv2-text-green-600 hover:ppv2-bg-green-100 ppv2-rounded ppv2-transition-all"
                                                    @click="saveEditing()"
                                                    :disabled="savingEdit"
                                                >
                                                    <Check :size="16" />
                                                </button>
                                                <button
                                                    class="ppv2-p-1 ppv2-text-red-500 hover:ppv2-bg-red-100 ppv2-rounded ppv2-transition-all"
                                                    @click="cancelEditing()"
                                                    :disabled="savingEdit"
                                                >
                                                    <X :size="16" />
                                                </button>
                                            </template>
                                            <template v-else>
                                                <button
                                                    class="ppv2-p-1 ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C] hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                                                    @click="handleRemoveItem(child.id, group)"
                                                    :disabled="editingItemId !== null || quoteLocked"
                                                >
                                                    <Trash2 :size="14" />
                                                </button>
                                            </template>
                                        </div>
                                    </td>
                                </tr>
                                <!-- « + ajouter une option » sous la dernière option, révélé au
                                     survol du groupe : rattache une nouvelle option au MÊME parent
                                     (produit ou menu resto). N'apparaît que là où l'ajout d'option
                                     a un sens et qu'il y a déjà au moins une option. -->
                                <tr
                                    v-if="canAddOption(group.parent) && group.children.length > 0"
                                    class="is-child add-option-row"
                                >
                                    <td class="col-grip ppv2-w-10"></td>
                                    <td class="col-desc" colspan="8">
                                        <button
                                            type="button"
                                            class="add-option-inline"
                                            :disabled="addingCustomLine || editingItemId !== null || cellEdit.saving"
                                            @click="addOption(group.parent)"
                                        >
                                            <Plus :size="14" />
                                            <span>{{ $t('devis.v3.add_option') }}</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                            <!-- Brouillon LOCAL d'une ligne personnalisée : rendu uniquement côté
                                 client tant que `customDraft` est non-null. Tous les champs sont des
                                 inputs ; naviguer de l'un à l'autre DANS la ligne ne crée rien. La
                                 ligne n'est écrite en base qu'au commit (blur hors de la ligne,
                                 Entrée, ou bouton ✓). Mêmes colonnes / mêmes classes d'édition que
                                 les lignes existantes. `ref="customDraftRow"` porte la détection de
                                 focus (contains(document.activeElement)). -->
                            <tbody v-if="customDraft" class="item-group draft-line-body">
                                <tr ref="customDraftRow" :class="{ 'is-optional': customDraft.is_optional }">
                                    <td class="col-grip ppv2-w-10"></td>
                                    <td class="col-desc">
                                        <div class="item-desc draft-suggest-wrap">
                                            <input
                                                ref="customDraftDesignation"
                                                type="text"
                                                v-model="customDraft.custom_label"
                                                :placeholder="$t('devis.v3.custom_line_default')"
                                                :disabled="creatingDraft"
                                                @input="onDraftDesignationInput"
                                                @blur="onDraftBlur"
                                                @keydown.enter.prevent="commitDraft"
                                                style="border: 1px solid #7d2dbd !important"
                                                class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-full ppv2-outline-none"
                                            />
                                            <!-- Autocomplete catalogue (≥ 3 car.) : suggère un article existant.
                                                 @mousedown.prevent → remplit la désignation SANS blurrer l'input
                                                 (donc sans déclencher le commit de la ligne). -->
                                            <ul v-if="draftSuggestOpen && draftHasSuggestions" class="draft-suggest">
                                                <template v-for="grp in draftSuggestionGroups">
                                                    <li v-if="grp.items.length" :key="grp.key" class="draft-suggest__head">
                                                        {{ grp.label }}
                                                    </li>
                                                    <li
                                                        v-for="it in grp.items"
                                                        :key="grp.key + '-' + it.id"
                                                        class="draft-suggest__item"
                                                        @mousedown.prevent="pickDraftSuggestion(it, grp.key)"
                                                    >
                                                        <span class="draft-suggest__name">{{ it.name }}</span>
                                                        <span class="draft-suggest__price">{{ formatCurrency(it.price) }}</span>
                                                    </li>
                                                </template>
                                            </ul>
                                        </div>
                                    </td>
                                    <td class="col-optional">
                                        <input
                                            type="checkbox"
                                            class="option-checkbox"
                                            v-model="customDraft.is_optional"
                                            :disabled="creatingDraft"
                                            :title="$t('devis.v3.optional_line')"
                                            @blur="onDraftBlur"
                                            @keydown.enter.prevent="commitDraft"
                                        />
                                    </td>
                                    <td class="col-qty">
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            v-model.number="customDraft.quantity"
                                            :disabled="creatingDraft"
                                            @blur="onDraftBlur"
                                            @keydown.enter.prevent="commitDraft"
                                            style="border: 1px solid #7d2dbd !important"
                                            class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-2 ppv2-py-2 ppv2-w-20 ppv2-text-center ppv2-outline-none"
                                        />
                                    </td>
                                    <td class="col-price">
                                        <div class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                            <input
                                                type="number"
                                                step="1"
                                                min="0"
                                                v-model.number="customDraft.price_ht"
                                                :disabled="creatingDraft"
                                                @blur="onDraftBlur"
                                                @keydown.enter.prevent="commitDraft"
                                                style="border: 1px solid #7d2dbd !important"
                                                class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-w-28 ppv2-text-right ppv2-outline-none"
                                            />
                                            <span class="poppinsMedium">€</span>
                                        </div>
                                    </td>
                                    <!-- Remise non éditable à la création : affichée à 0 %, comme une
                                         ligne neuve. Elle se règle ensuite via le crayon de cellule. -->
                                    <td class="col-discount">
                                        <span class="poppinsMedium">0%</span>
                                    </td>
                                    <td class="col-tva">
                                        <div class="ppv2-flex ppv2-items-center ppv2-gap-1 ppv2-justify-center">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.1"
                                                v-model.number="customDraft.vat_rate"
                                                :disabled="creatingDraft"
                                                @blur="onDraftBlur"
                                                @keydown.enter.prevent="commitDraft"
                                                style="border: 1px solid #7d2dbd !important"
                                                class="ppv2-border ppv2-border-solid ppv2-border-primary ppv2-text-[#1d1d1b] poppinsRegular ppv2-rounded-lg ppv2-px-2 ppv2-py-2 ppv2-w-16 ppv2-text-center ppv2-outline-none"
                                            />
                                            <span class="poppinsMedium">%</span>
                                        </div>
                                    </td>
                                    <!-- Total muet : aucun recalcul côté client (R3). Le serveur le
                                         renvoie après le commit. -->
                                    <td class="col-total">
                                        <div class="col-total-action"></div>
                                    </td>
                                    <td class="col-actions">
                                        <div class="row-actions">
                                            <!-- ✓ / ✗ optionnels : le commit se fait aussi au blur hors
                                                 de la ligne et sur Entrée. Boutons DANS la ligne : leur
                                                 focus est « à l'intérieur » (contains), donc le blur
                                                 d'un input ne déclenche pas un second commit. -->
                                            <button
                                                type="button"
                                                class="ppv2-p-1 ppv2-text-green-600 hover:ppv2-bg-green-100 ppv2-rounded ppv2-transition-all"
                                                :disabled="creatingDraft"
                                                :title="$t('devis.v3.draft_validate')"
                                                @click="commitDraft"
                                            >
                                                <Check :size="16" />
                                            </button>
                                            <button
                                                type="button"
                                                class="ppv2-p-1 ppv2-text-red-500 hover:ppv2-bg-red-100 ppv2-rounded ppv2-transition-all"
                                                :disabled="creatingDraft"
                                                :title="$t('devis.v3.cancel')"
                                                @mousedown.prevent
                                                @click="cancelDraft"
                                            >
                                                <X :size="16" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                            <!-- « + Ajouter une ligne » : dernier corps du tableau, donc TOUJOURS
                                 après la ligne « + Ajouter une option » du dernier groupe, celle-ci
                                 restant en place. tbody dédié et hors de la boucle : le v-for de
                                 l.244 porte le tbody, un <tr> placé directement sous <table> serait
                                 sorti du tableau par le parseur HTML. -->
                            <tbody class="table-add-line-body">
                                <tr>
                                    <td colspan="9" class="table-add-line">
                                        <v-tooltip bottom :disabled="!!selectedClient">
                                            <template v-slot:activator="{ on, attrs }">
                                                <!-- Le <span> porte le tooltip : un <button> natif
                                                     :disabled n'émet plus d'événement pointeur,
                                                     le message n'apparaîtrait jamais. -->
                                                <span v-on="on" v-bind="attrs" :class="{ 'is-disabled': !selectedClient }">
                                                    <button
                                                        type="button"
                                                        class="table-add-line__btn"
                                                        :disabled="
                                                            !selectedClient ||
                                                            addingCustomLine ||
                                                            editingItemId !== null ||
                                                            customDraft !== null ||
                                                            quoteLocked
                                                        "
                                                        @click="startCustomDraft"
                                                    >
                                                        <Plus :size="11" :stroke-width="2.6" />
                                                        <span>{{ $t('devis.v3.add_line') }}</span>
                                                    </button>
                                                </span>
                                            </template>
                                            <span>{{ $t('devis.v3.must_select_client') }}</span>
                                        </v-tooltip>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="table-footer md:ppv2-p-4 ppv2-p-2">
                        <div class="table-totals">
                            <!-- Remise NIVEAU DEVIS. Un devis n'enregistre qu'un CODE : aucune
                                 remise n'est calculée ni appliquée ici (elle se matérialise à la
                                 conversion, en fail-open). La ligne « code appliqué » annonce donc
                                 une INTENTION (« Code promo X · 10 % ») et ne modifie aucun
                                 total — la ligne « Remise spécifique » reste la seule remise
                                 chiffrée du bloc. -->
                            <!-- Devis accepté : un contrôle `disabled` n'émet pas d'évènement de
                                 survol, donc le `title` d'un ancêtre ne s'affiche jamais. On enrobe
                                 tout le bloc remise d'un <span> NON désactivé qui porte le tooltip ;
                                 `quote-locked-btn` neutralise les contrôles internes (pointer-events
                                 none + opacité) quand le devis est verrouillé. -->
                            <span class="discount-lock-wrap" :class="{ 'quote-locked-btn': quoteLocked }" :title="lockTitle">
                                <div v-if="discountCode && !discountEditing" class="discount-applied">
                                    <button
                                        type="button"
                                        class="discount-applied__label"
                                        :disabled="quoteLocked"
                                        @click="openDiscountEditor"
                                    >
                                        {{ $t('devis.v3.discount_code') }}
                                        <span class="discount-applied__code">{{ discountCode }}</span>
                                        <span v-if="selectedPromo" class="discount-applied__value">
                                            {{ formatPromoValue(selectedPromo) }}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        class="discount-editor__cancel"
                                        :title="$t('devis.v3.remove_discount')"
                                        :disabled="quoteLocked"
                                        @click="cancelDiscount"
                                    >
                                        <X :size="14" />
                                    </button>
                                </div>

                                <!-- État 1 : lien « Appliquer une remise ». État 2 : ligne d'édition
                                 (libellé + × + popover de codes groupés par type). -->
                                <button
                                    v-if="!discountEditing"
                                    type="button"
                                    class="discount-link poppinsMedium ppv2-text-xs"
                                    :disabled="quoteLocked"
                                    @click="openDiscountEditor"
                                >
                                    {{ $t('devis.v3.apply_discount') }}
                                </button>
                                <div v-else class="discount-editor">
                                    <span class="discount-editor__label ppv2-text-xs poppinsSemiBold">{{
                                        $t('devis.v3.discount')
                                    }}</span>
                                    <button
                                        type="button"
                                        class="discount-editor__cancel"
                                        :title="$t('devis.v3.remove_discount')"
                                        @click="cancelDiscount"
                                    >
                                        <X :size="14" />
                                    </button>
                                    <div class="discount-editor__controls">
                                        <!-- `v-click-outside` est une directive LOCALE (cf.
                                         ClientSearchSelect) : le projet ne charge pas celle de
                                         Vuetify. -->
                                        <div
                                            v-click-outside="closeDiscountCodeList"
                                            class="discount-code"
                                            @keydown.esc="closeDiscountCodeList"
                                        >
                                            <button
                                                type="button"
                                                class="discount-code__pill"
                                                :class="{ 'is-active': !!discountCode }"
                                                :aria-expanded="discountCodeListOpen ? 'true' : 'false'"
                                                @click="toggleDiscountCodeList"
                                            >
                                                <Tag :size="12" />
                                                <span class="discount-code__pill-label">
                                                    {{ discountCode || $t('devis.v3.discount_code') }}
                                                </span>
                                                <ChevronDown :size="12" class="discount-code__chevron" />
                                            </button>
                                            <div v-if="discountCodeListOpen" class="discount-code__list">
                                                <div
                                                    v-for="group in discountCodeGroups"
                                                    :key="group.type"
                                                    class="discount-code__group"
                                                >
                                                    <p class="discount-code__group-label">{{ $t(group.labelKey) }}</p>
                                                    <button
                                                        v-for="promo in group.items"
                                                        :key="promo.id"
                                                        type="button"
                                                        class="discount-code__item"
                                                        :class="{ 'is-selected': promo.code === discountCode }"
                                                        :title="promo.title || promo.code"
                                                        @click="selectPromoCode(promo.code)"
                                                    >
                                                        <span class="discount-code__item-label">{{ promo.code }}</span>
                                                        <span class="discount-code__item-value">
                                                            {{ formatPromoValue(promo) }}
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- Saisie visuelle uniquement : elle n'est pas persistée ni appliquée aux totaux. -->
                                        <span class="discount-manual-or">{{ $t('devis.v3.or') }}</span>
                                        <input
                                            v-model.number="manualDiscountValue"
                                            type="number"
                                            min="0"
                                            :max="manualDiscountMode === 'percentage' ? 100 : null"
                                            :step="manualDiscountMode === 'percentage' ? '0.1' : '0.01'"
                                            inputmode="decimal"
                                            class="discount-manual-input"
                                            :aria-label="$t('devis.v3.manual_discount')"
                                            :placeholder="$t('devis.v3.discount_amount_placeholder')"
                                        />
                                        <div class="discount-manual-mode" role="group" :aria-label="$t('devis.v3.discount_mode')">
                                            <button
                                                type="button"
                                                :class="{ 'is-active': manualDiscountMode === 'percentage' }"
                                                :aria-pressed="manualDiscountMode === 'percentage' ? 'true' : 'false'"
                                                :title="$t('devis.v3.discount_percentage')"
                                                @click="manualDiscountMode = 'percentage'"
                                            >
                                                %
                                            </button>
                                            <button
                                                type="button"
                                                :class="{ 'is-active': manualDiscountMode === 'amount' }"
                                                :aria-pressed="manualDiscountMode === 'amount' ? 'true' : 'false'"
                                                :title="$t('devis.v3.discount_amount')"
                                                @click="manualDiscountMode = 'amount'"
                                            >
                                                €
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </span>

                            <div class="total-row poppinsSemiBold ppv2-text-xs ppv2-gap-6">
                                <span>{{ $t('devis.v3.total_ht') }}</span>
                                <strong>{{ formatCurrency(totals.ht) }}</strong>
                            </div>
                            <div v-if="(totals.discount || 0) > 0.005" class="total-row poppinsSemiBold ppv2-text-xs ppv2-gap-6">
                                <span>{{
                                    discountedItemCount > 1 ? $t('devis.v3.remises_specific') : $t('devis.v3.remise_specific')
                                }}</span>
                                <strong>{{ formatCurrency(totals.discount) }}</strong>
                            </div>
                            <div class="total-row poppinsMedium ppv2-text-xs ppv2-gap-6">
                                <span>{{ $t('devis.v3.tva') }}</span>
                                <strong>{{ formatCurrency(totals.tax) }}</strong>
                            </div>
                            <div class="total-divider"></div>
                            <div class="total-row ppv2-text-sm poppinsBold ppv2-gap-6">
                                <span>{{ $t('devis.v3.total_ttc') }}</span>
                                <strong>{{ formatCurrency(totals.ttc) }}</strong>
                            </div>

                            <button
                                v-if="!depositEditing"
                                type="button"
                                class="deposit-box"
                                :disabled="quoteLocked"
                                @click="openDepositEditor"
                            >
                                <span class="deposit-box__text">
                                    <span class="deposit-box__title poppinsSemiBold">{{ $t('devis.v3.deposit_title') }}</span>
                                    <span class="deposit-box__subtitle poppinsRegular">{{
                                        $t('devis.v3.deposit_subtitle')
                                    }}</span>
                                </span>
                                <ChevronRight :size="18" class="deposit-box__chevron" />
                            </button>
                            <div v-else class="deposit-editor">
                                <span class="discount-editor__label ppv2-text-xs poppinsSemiBold">{{
                                    $t('devis.v3.deposit_label')
                                }}</span>
                                <button
                                    type="button"
                                    class="discount-editor__cancel"
                                    :title="$t('devis.v3.remove_deposit')"
                                    @click="cancelDeposit"
                                >
                                    <X :size="14" />
                                </button>
                                <span class="deposit-editor__spacer"></span>
                                <input
                                    ref="depositInput"
                                    v-model.number="depositValue"
                                    type="number"
                                    min="0"
                                    :max="depositMode === 'percentage' ? 100 : null"
                                    :step="depositMode === 'percentage' ? '0.1' : '0.01'"
                                    inputmode="decimal"
                                    class="deposit-editor__input"
                                    :aria-label="$t('devis.v3.deposit_title')"
                                    :placeholder="$t('devis.v3.deposit_amount_placeholder')"
                                />
                                <div class="discount-manual-mode" role="group" :aria-label="$t('devis.v3.deposit_mode')">
                                    <button
                                        type="button"
                                        :class="{ 'is-active': depositMode === 'percentage' }"
                                        :aria-pressed="depositMode === 'percentage' ? 'true' : 'false'"
                                        :title="$t('devis.v3.discount_percentage')"
                                        @click="depositMode = 'percentage'"
                                    >
                                        %
                                    </button>
                                    <button
                                        type="button"
                                        :class="{ 'is-active': depositMode === 'amount' }"
                                        :aria-pressed="depositMode === 'amount' ? 'true' : 'false'"
                                        :title="$t('devis.v3.discount_amount')"
                                        @click="depositMode = 'amount'"
                                    >
                                        €
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Footer Details -->
                <div
                    class="canvas-footer ppv2-flex md:ppv2-flex-row ppv2-flex-col ppv2-justify-between ppv2-gap-6 ppv2-px-6 ppv2-py-8"
                >
                    <div class="footer-section">
                        <h5>{{ $t('devis.v3.payment_conditions') }}</h5>
                        <p v-if="selectedPaymentMethods.length > 0" class="poppinsMedium">
                            {{ selectedPaymentMethods.map((m) => m.label).join(' / ') }}
                        </p>
                        <p v-else>{{ $t('devis.v3.no_payment_method_selected') }}</p>
                        <!-- Le RIB n'a de sens que si le virement est proposé. -->
                        <template v-if="isTransferSelected">
                            <p v-if="bank.iban">{{ $t('devis.v3.iban_label') }} {{ bank.iban }}</p>
                            <p v-if="bank.swift">{{ $t('devis.v3.swift_label') }} {{ bank.swift }}</p>
                        </template>
                    </div>
                    <div class="footer-section" :title="lockTitle">
                        <h5>{{ $t('devis.v3.notes_header') }}</h5>
                        <!-- Devis accepté : la note du bas passe en lecture seule. -->
                        <InlineNoteField
                            v-model="note"
                            :readonly="quoteLocked"
                            :placeholder="$t('devis.v3.note_field_placeholder')"
                        />
                    </div>
                    <div v-if="form.e_signature || quoteSignature" class="footer-section sig">
                        <h5>{{ $t('devis.v3.signature_header') }}</h5>
                        <!-- Devis signé par le client : on affiche la vraie signature (image + nom + date).
                             Sinon : zone de signature vierge (comme avant). -->
                        <template v-if="quoteSignature">
                            <img
                                v-if="quoteSignature.image"
                                :src="quoteSignature.image"
                                :alt="$t('devis.v3.signature_alt')"
                                class="quote-signature__img"
                            />
                            <div class="quote-signature__meta">
                                {{ $t('devis.v3.signed_by', { name: quoteSignature.name, date: quoteSignature.date }) }}
                            </div>
                        </template>
                        <div v-else class="ppv2-w-[190px] ppv2-h-[70px] bg-purple ppv2-bg-opacity-10 ppv2-rounded-lg"></div>
                    </div>
                </div>
            </div>

            <QuoteOptionsPanel
                :form="form"
                :isOpen="isSidebarOpen"
                :templates="templates"
                :payment-methods="paymentMethods"
                :bank="bank"
                :bank-errors="bankErrors"
                @toggle="isSidebarOpen = !isSidebarOpen"
                @change-template="saveTemplate"
                @bank-draft="updateBankDraft"
                @clear-bank-error="clearBankError"
            />
        </div>

        <!-- V3 Standard Modals -->
        <ClientSelectorModal
            :visible.sync="showClientSelector"
            :prefill-name="clientPrefillName"
            :client-id="clientEditId"
            @selected="handleClientSelected"
            @create-new="handleCreateClient"
        />

        <ConfirmationDialog
            :isOpen="pendingRemoval !== null"
            :title="$t('devis.v3.remove_item_title')"
            :message="pendingRemoval ? pendingRemoval.message : ''"
            type="danger"
            confirmButtonVariant="danger"
            :confirmText="$t('devis.v3.remove_item_confirm')"
            @confirm="confirmRemoval"
            @cancel="cancelRemoval"
        />

        <!-- Le planning n'est PAS borné par l'échéance du devis : celle-ci est le délai
             d'acceptation laissé au client, pas une limite de planification. Chiffrer une
             prestation postérieure à l'échéance est légitime (cf. QuoteScheduleGuard).
             La contrainte inverse — raccourcir la validité sous une ligne déjà posée —
             reste appliquée, voir le watcher `form.validity`. -->
        <QuoteItemStepper
            :visible.sync="showItemStepper"
            :uses-time-slots="usesTimeSlots"
            @add="handleAddItem"
            @add-batch="handleAddBatch"
            @add-foods="handleAddFoodItems"
            @add-products="handleAddProductItems"
        />

        <!-- Même popup que la création, ouverte sur l'étape Planning avec le créneau courant.
             Instance séparée pour ne jamais interférer avec l'état du flux d'ajout. -->
        <QuoteItemStepper
            v-if="editingReservationItem"
            :visible.sync="showSlotEditor"
            :uses-time-slots="usesTimeSlots"
            :editing-item="editingReservationItem"
            @update="handleUpdateItemReservation"
        />

        <QuoteModal :order="order_id" ref="devisPopup" />

        <!-- Popup d'envoi personnalisé : ne pilote que le CORPS du message,
             destinataire / objet / PDF joint restent décidés côté serveur. -->
        <DevisSendModal v-model="showSendModal" :quote="sendModalQuote" />
    </div>
</template>

<script>
import ClientSelectorModal from './ClientSelectorModal.vue'
import ClientSearchSelect from './components/ClientSearchSelect.vue'
import QuoteItemStepper from './QuoteItemStepper.vue'
import QtyStepper from './components/QtyStepper.vue'
import QuoteOptionsPanel from './QuoteOptionsPanel.vue'
import QuoteModal from './Quotemodal.vue'
import DraggablePostIt from './components/DraggablePostIt.vue'
import DevisSendModal from '../DevisSendModal.vue'
import InlineNoteField from './components/InlineNoteField.vue'
import ApiManager from '../../Checkout_BO/CheckoutOverlay/_core/ApiManager'
import normalizeFontSize from '../../../utils/normalizeFontSize.js'
import {
    FileMinus,
    FileText,
    Save,
    X,
    Check,
    Pencil,
    RotateCcw,
    Send,
    FilePen,
    ChevronDown,
    ChevronRight,
    Plus,
    Trash2,
    CopyPlus,
    StickyNote,
    User,
    GripVertical,
    Lock,
    ExternalLink,
    LayoutGrid,
    Tag,
} from 'lucide-vue'
import Sortable from 'sortablejs'
import BaseSplitButton from '../../Shared/BaseSplitButton.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import ConfirmationDialog from '../../Shared/ConfirmationDialog.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'

// Dates du devis : on reste en chaînes 'YYYY-MM-DD' sur le calendrier LOCAL.
// `toISOString()` renvoie une date UTC et décalerait l'émission d'un jour en soirée,
// donc l'échéance et toutes les comparaisons avec les dates des lignes.
function toLocalDate(date) {
    const pad = (n) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function addDays(dateStr, days) {
    const [year, month, day] = dateStr.substring(0, 10).split('-').map(Number)
    const shifted = new Date(Date.UTC(year, month - 1, day))
    shifted.setUTCDate(shifted.getUTCDate() + days)
    return shifted.toISOString().substring(0, 10)
}

function daysBetween(fromStr, toStr) {
    const [fy, fm, fd] = fromStr.substring(0, 10).split('-').map(Number)
    const [ty, tm, td] = toStr.substring(0, 10).split('-').map(Number)
    return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86400000)
}

export default {
    props: {
        initialOrder: {
            type: Object,
            default: () => null,
        },
    },
    directives: {
        // Même directive locale que ClientSearchSelect / DateRangePicker : le projet ne charge pas
        // celle de Vuetify. Sert à fermer le popover des codes promo au clic extérieur.
        'click-outside': {
            bind(el, binding, vnode) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        vnode.context[binding.expression](event)
                    }
                }
                document.body.addEventListener('click', el.clickOutsideEvent)
            },
            unbind(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent)
            },
        },
    },
    components: {
        CustomInputUpload,
        ClientSelectorModal,
        ClientSearchSelect,
        QuoteItemStepper,
        QtyStepper,
        QuoteOptionsPanel,
        QuoteModal,
        DevisSendModal,
        DraggablePostIt,
        InlineNoteField,
        FileMinus,
        FileText,
        Save,
        X,
        Check,
        Pencil,
        RotateCcw,
        Send,
        FilePen,
        ChevronDown,
        ChevronRight,
        Plus,
        Trash2,
        CopyPlus,
        StickyNote,
        User,
        GripVertical,
        Lock,
        ExternalLink,
        LayoutGrid,
        Tag,
        BaseSplitButton,
        BaseSelect,
        ConfirmationDialog,
    },
    data() {
        return {
            Send,
            saving: false,
            // Base d'affichage des colonnes PU / Total du tableau : 'ht' (défaut) ou 'ttc'.
            // Purement visuel — ne modifie ni les données de ligne ni ce qui est envoyé au serveur.
            priceDisplayMode: 'ht',
            priceDisplayMenuOpen: false,
            showItemStepper: false,
            // Ajout d'une ligne personnalisée en cours (neutralise le double clic).
            addingCustomLine: false,
            // Ligne personnalisée à peine créée et pas encore renseignée : annuler son édition
            // la RETIRE, pour qu'un clic « Ajouter » par erreur ne laisse pas de ligne fantôme.
            justCreatedCustomLineId: null,
            // Mode « capture désignation » : seul le champ nom est affiché, la validation
            // se fait sur Entrée ou blur (pas de bouton ✓). Valeur = id de la ligne en capture.
            customLineCaptureId: null,
            // Guard anti double-fire du auto-save (blur + Enter rapide).
            captureSaving: false,
            // Brouillon LOCAL d'une ligne personnalisée : tant qu'il est non-null, la ligne
            // n'existe QUE côté client (aucun appel serveur). On la crée en base seulement au
            // commit (blur hors de la ligne / Entrée / bouton ✓). `null` = pas de brouillon.
            customDraft: null,
            // Guard anti double-commit du brouillon (blur + Entrée quasi simultanés, ou
            // clic ✓ pendant que le blur arme déjà commitDraft).
            creatingDraft: false,
            // Autocomplete catalogue sur la désignation du brouillon (≥ 3 car.) : suggestions
            // groupées (option C → remplit juste le libellé, aucun ajout d'article).
            draftSuggestions: { experiences: [], products: [], foods: [] },
            draftSuggestOpen: false,
            draftSearchTimer: null,
            showClientSelector: false,
            // Nom saisi dans la recherche au moment où la création a été demandée : le
            // popup s'ouvre avec, plutôt que de faire retaper ce qui vient de l'être.
            clientPrefillName: '',
            // Fiche que le popup doit ouvrir en modification ; `null` pour une création.
            clientEditId: null,
            changingClient: false,
            showSendDropdown: false,
            showSendModal: false,
            sendingQuote: false,
            selectedClient: null,
            note: '',
            clientRequest: '',
            postItVisible: true,
            // Suppression en attente de confirmation : { orderItemId, message }.
            pendingRemoval: null,
            // Note de ligne en cours de saisie.
            noteEditingId: null,
            noteDraft: '',
            savingNote: false,
            items: [],
            editingItemId: null,
            savingEdit: false,
            // Ligne dont la quantité part au serveur via le stepper (neutralise le double clic).
            qtyUpdatingId: null,
            // Ligne dont la case « Option » (is_optional) part au serveur (neutralise le double clic).
            optionUpdatingId: null,
            // Édition d'UNE case (désignation ou P.U. HT), déclenchée par le crayon au survol de
            // la case. Indépendante du mode édition de ligne (editingItemId), qui reste la porte
            // vers la date et les participants d'une prestation.
            cellEdit: { id: null, field: null, value: '', saving: false },
            editForm: {
                custom_label: '',
                quantity: 1,
                price_ht: 0,
                vat_rate: 0,
                // Clé déclarée dès l'état initial pour la réactivité Vue 2 (ajout a posteriori
                // sur un objet existant ne serait pas réactif sans $set).
                is_optional: false,
            },
            // Choix de participants proposés pendant l'édition d'une ligne.
            editQuantityOptions: [],
            loadingEditOptions: false,
            // Ligne dont on modifie le créneau via la popup de planning.
            editingReservationItem: null,
            showSlotEditor: false,
            initialLogo: null,
            logoFromEstablishment: false,
            duplicatingItemId: null,
            order_id: null,
            templates: [],
            // Date d'émission du devis : ancre de calcul de l'échéance. Sur un devis
            // existant, c'est sa date de création (recalculer depuis aujourd'hui déplacerait
            // silencieusement l'échéance déjà communiquée au client).
            issueDate: toLocalDate(new Date()),
            // Garde-fou du watcher `form.validity` : une remise à la valeur précédente (refus)
            // ou un alignement au chargement ne doit pas relancer le calcul ni le contrôle.
            skipValidityWatch: false,
            // Même garde-fou pour le délai de relance : réafficher la valeur enregistrée ne
            // doit pas déclencher un nouvel enregistrement (boucle en cas d'échec serveur).
            skipAutoReminderWatch: false,
            form: {
                logo: null,
                client_id: null,
                validity: 30,
                // Jours avant échéance déclenchant la relance automatique de CE devis
                // (7 / 3 / 1). Valeur enregistrée telle quelle et lue par
                // AutoRelaunchDevisJob. Le 7 est le même défaut que la colonne
                // devis_invoices.auto_relaunch_days : l'écran n'invente rien.
                auto_reminder: 7,
                template_id: 1,
                e_signature: true,
                block_resources: 'none',
                // Sélection des moyens de paiement proposés sur le devis, indexée par
                // id de payment_method (source dynamique : Establishment::payments,
                // même référentiel que la caisse nomade).
                //
                // On repart de la sélection ENREGISTRÉE sur le devis quand elle existe ;
                // `null` (jamais renseignée, ou devis pas encore créé) = tous activés,
                // comportement d'origine. Une liste vide enregistrée reste vide : ne
                // proposer aucun moyen est un choix, pas une absence de choix.
                payments: (() => {
                    const stored = this.initialOrder?.selected_payment_methods
                    const selected = Array.isArray(stored) ? stored.map(Number) : null

                    return (this.initialOrder?.payment_methods || []).reduce((acc, m) => {
                        acc[m.id] = selected ? selected.includes(Number(m.id)) : true
                        return acc
                    }, {})
                })(),
                // Sous-option spécifique au virement (e-virement / paiement en ligne).
                e_virement: false,
                notes: '',
                // Toujours dérivée : émission + validité. Jamais saisie directement.
                due_date: addDays(toLocalDate(new Date()), 30),
                establishment_id: window.establishmentId || 1,
            },
            sortableInstance: null,
            isSavingPositions: false,
            // Debounce de l'enregistrement des moyens de paiement.
            paymentsSaveTimer: null,
            isSidebarOpen: true,
            // RIB enregistré depuis le devis (première saisie) : renvoyé par le serveur,
            // il prend le pas sur la valeur injectée au chargement de la page.
            bankOverride: {},
            // Saisie en cours dans le panneau latéral, persistée au clic sur "Enregistrer".
            bankDraft: {},
            bankErrors: {},
            savingBank: false,
            // TOUTES les clés sont déclarées ici, y compris `net_ht` et `discount` :
            // `updateTotalsFromOrder` les écrit, mais une clé ajoutée après coup sur un objet
            // déjà observé n'est pas réactive en Vue 2 — et surtout, tant qu'aucune ligne
            // n'existe la fonction n'a jamais tourné, `discount` valait `undefined` et la
            // ligne « Remise » affichait « NaN € ».
            totals: {
                ht: 0,
                net_ht: 0,
                tax: 0,
                ttc: 0,
                discount: 0,
            },
            // Remise NIVEAU DEVIS — le code promo CHOISI est persisté sur
            // `devis_invoices.promo_code`, mais AUCUNE remise n'est calculée ni appliquée à ce
            // stade : la remise se matérialise à la conversion du devis (QuotePromotionService),
            // en fail-open. Le bloc affiche donc une INTENTION (« Code promo X · 10 % »), jamais
            // une déduction en €, et ne touche pas aux totaux.
            discountEditing: false, // false = état 1 (lien), true = état 2 (ligne d'édition)
            discountCode: null, // code promo choisi, injecté par le serveur à l'ouverture
            discountCodeListOpen: false, // popover de codes ouvert
            promoCodes: [], // liste des codes promo proposables (injectée par le serveur)
            manualDiscountValue: null, // saisie visuelle non persistée
            manualDiscountMode: 'percentage',
            depositEditing: false,
            depositValue: null,
            depositMode: 'percentage',
        }
    },
    watch: {
        items: {
            handler() {
                this.$nextTick(() => this.initSortable())
            },
            deep: true,
        },
        // Moyens de paiement : chaque bascule est enregistrée sur le devis. Sans cela la
        // sélection ne vivait que dans le navigateur et revenait « tout coché » au F5.
        // Debounce : basculer plusieurs interrupteurs d'affilée ne fait qu'UN appel.
        'form.payments': {
            deep: true,
            handler() {
                this.schedulePaymentMethodsSave()
            },
        },
        // Délai de relance automatique : enregistré sur le devis à chaque changement, comme
        // l'échéance et les moyens de paiement. Sans cela le sélecteur ne pilotait rien —
        // AutoRelaunchDevisJob appliquait sa fenêtre unique à tous les devis du tenant.
        // Pas de debounce : un select ne produit qu'un changement à la fois.
        'form.auto_reminder'(days, previousDays) {
            if (this.skipAutoReminderWatch) {
                this.skipAutoReminderWatch = false
                return
            }
            this.persistAutoRelaunchDays(previousDays)
        },
        // Devis pas encore créé au moment du choix : la sélection part dès que la première
        // ligne fait naître le devis (c'est elle qui crée l'enregistrement DevisInvoice).
        order_id(newId, previousId) {
            if (newId && !previousId) {
                this.persistPaymentMethods()
                this.persistAutoRelaunchDays(null)
            }
        },
        // Fermer la popup de créneau — croix, Échap, clic hors modale — sort aussi la ligne du
        // mode édition, exactement comme après un enregistrement. Une bordure violette laissée
        // ouverte laisserait croire qu'une saisie reste à valider.
        showSlotEditor(open) {
            if (!open) {
                this.cancelEditing()
            }
        },
        // L'échéance découle de la durée de validité : émission + N jours.
        // Seul un RACCOURCISSEMENT sous la dernière prestation déjà chiffrée est refusé :
        // il périmerait un devis déjà planifié. Allonger la validité (15 j → 30 j) passe
        // toujours, même si la nouvelle échéance ne couvre pas encore la prestation — le
        // commercial va dans le bon sens, et depuis qu'une ligne peut être planifiée
        // au-delà de l'échéance, le bloquer l'enfermerait sans issue.
        // Le serveur applique exactement la même règle (QuoteScheduleGuard) : le front ne
        // fait qu'éviter un aller-retour et expliquer le blocage.
        'form.validity'(days, previousDays) {
            if (this.skipValidityWatch) {
                this.skipValidityWatch = false
                return
            }

            const dueDate = addDays(this.issueDate, Number(days))
            const latest = this.latestItemDate
            // Comparaison sur les ÉCHÉANCES, pas sur les durées : même critère que le
            // serveur, qui ne connaît que `orders.due_date`. Chaînes 'YYYY-MM-DD',
            // triées lexicographiquement.
            const currentDueDate = this.form.due_date ? String(this.form.due_date).substring(0, 10) : null
            const isShortening = !!currentDueDate && dueDate < currentDueDate

            if (isShortening && latest && dueDate < latest) {
                // Avertissement (jaune) et non erreur (rouge) : c'est une règle de saisie
                // attendue, pas une panne. Un toast rouge se lit comme un bug applicatif.
                // Délai allongé : le message porte deux dates et doit pouvoir être lu.
                this.$toast.warning(
                    this.$t('devis.v3.due_date_before_last_item', {
                        due: this.formatDate(dueDate),
                        item: this.formatDate(latest),
                    }),
                    { timeout: 6000 }
                )
                this.skipValidityWatch = true
                this.form.validity = previousDays
                return
            }

            const previousDueDate = this.form.due_date
            this.form.due_date = dueDate
            this.persistDueDate(previousDays, previousDueDate)
        },
        // Mode capture : focus automatique sur le champ désignation dès qu'une ligne
        // entre en mode capture (après la création AJAX et le rendu du template).
        customLineCaptureId(newId) {
            if (newId) {
                this.$nextTick(() => {
                    const input = this.$refs.captureInput
                    if (input) {
                        ;(Array.isArray(input) ? input[0] : input).focus()
                    }
                })
            }
        },
    },
    computed: {
        /**
         * Devis ACCEPTÉ / SIGNÉ (« Gagné ») : le serveur renseigne `quote_accepted_at`.
         * L'éditeur s'ouvre alors en LECTURE SEULE — toutes les actions d'édition sont
         * neutralisées, SAUF la bascule « Option » (le serveur l'autorise encore via
         * QuoteModificationGuard::assertCanToggleOptional). Simple confort d'affichage :
         * la vérité reste serveur, qui refuse toute autre modification.
         */
        quoteLocked() {
            return !!this.initialOrder?.quote_accepted_at
        },
        // Libellé du tooltip natif affiché près du curseur au survol des zones verrouillées.
        lockTitle() {
            return this.quoteLocked ? this.$t('devis.v3.locked_accepted') : null
        },
        // Preuve de signature client (nom, date, image base64) envoyée par le serveur, ou null si le
        // devis n'est pas signé. Sert à l'afficher dans le bandeau « accepté ».
        quoteSignature() {
            return this.initialOrder?.quote_signature || null
        },
        // Suggestions catalogue groupées pour l'autocomplete de la ligne perso.
        draftSuggestionGroups() {
            const s = this.draftSuggestions || {}
            // Ordre maquette : Expériences · Options (produits boutique) · Restauration.
            return [
                { key: 'experiences', label: this.$t('devis.v3.suggest_experiences'), items: s.experiences || [] },
                { key: 'products', label: this.$t('devis.v3.suggest_products'), items: s.products || [] },
                { key: 'foods', label: this.$t('devis.v3.suggest_foods'), items: s.foods || [] },
            ]
        },
        draftHasSuggestions() {
            return this.draftSuggestionGroups.some((g) => g.items.length > 0)
        },
        // Dernière date planifiée sur les lignes du devis ('YYYY-MM-DD'), ou null si aucune
        // ligne n'est datée. On regarde la fin autant que le début : une prestation à cheval
        // sur minuit se termine le lendemain.
        latestItemDate() {
            let latest = null
            ;(this.items || []).forEach((item) => {
                const dates = [item.start_time, item.end_time]
                dates.forEach((value) => {
                    if (!value) return
                    const date = String(value).substring(0, 10)
                    if (!latest || date > latest) latest = date
                })
            })
            return latest
        },
        /**
         * Données affichées par la popup d'envoi (client, numéro, pièce jointe).
         * Ce n'est qu'un rappel de ce que le serveur va envoyer : rien de tout cela
         * ne voyage dans la requête, seul le corps du message est transmis.
         */
        sendModalQuote() {
            return {
                order_id: this.order_id,
                serial_number: this.initialOrder?.quote_serial_number ?? null,
                user: this.selectedClient
                    ? {
                          id: this.selectedClient.id,
                          first_name: this.selectedClient.first_name,
                          last_name: this.selectedClient.last_name,
                          email: this.selectedClient.email,
                      }
                    : null,
            }
        },
        // Walk-in customer detection. The API flags it via `is_guest`; we also tolerate the
        // alternative `guest` / `is_passage` shapes for robustness across data sources.
        /**
         * Adresse de la fiche du client sélectionné.
         *
         * Même route que la liste clients et le tableau de bord — un seul endroit décide de
         * cette URL dans le projet, on la reproduit à l'identique plutôt que d'en inventer une.
         */
        customerRecordUrl() {
            return this.selectedClient ? `/back-office/v3/customer/${this.selectedClient.id}/edit` : null
        },
        isPassageClient() {
            return !!(
                this.selectedClient &&
                (this.selectedClient.is_guest === true ||
                    this.selectedClient.is_guest === 1 ||
                    this.selectedClient.guest === 1 ||
                    this.selectedClient.is_passage === true)
            )
        },
        // Moyens de paiement de l'établissement, injectés par le backend
        // (Establishment::payments, même source que la caisse nomade V3).
        paymentMethods() {
            return this.initialOrder?.payment_methods || []
        },
        /**
         * Moyens de paiement RETENUS pour ce devis — ce que le document affiche au client.
         *
         * Dérivé de `form.payments` : cocher ou décocher un interrupteur du panneau met à
         * jour le bloc « Conditions de paiement » immédiatement, sans attendre
         * l'enregistrement (qui suit de son côté, cf. persistPaymentMethods).
         *
         * Le libellé reprend celui du panneau (LABELS) pour qu'un moyen ne s'appelle pas
         * « CB » ici et « Carte bancaire » à côté.
         */
        selectedPaymentMethods() {
            const LABELS = {
                CB: 'Carte bancaire',
                'Pass culture': 'Pass Culture',
                'Points de fidélité': 'Fidélité',
            }

            return this.paymentMethods
                .filter((method) => this.form.payments[method.id])
                .map((method) => ({
                    id: method.id,
                    name: method.name,
                    label: LABELS[method.name] || method.display_name || method.name,
                }))
        },
        isTransferSelected() {
            return this.selectedPaymentMethods.some((method) => (method.name || '').toLowerCase() === 'virement')
        },
        // Mode de composition du devis, résolu côté serveur (devis figé sinon établissement).
        // Sert uniquement à adapter l'affichage : le serveur revalide tout de toute façon.
        // Défaut `true` = mode le plus contraint, pour ne rien assouplir par accident.
        usesTimeSlots() {
            return this.initialOrder?.make_devis_with_time_slot !== false
        },
        // Coordonnées bancaires de l'établissement (RIB affiché pour le virement).
        // Repris automatiquement des Paramètres ; `bankOverride` reflète une première
        // saisie qui vient d'être enregistrée côté serveur.
        bank() {
            const base = this.initialOrder?.bank || {
                bank_name: null,
                iban: null,
                swift: null,
                bank_address: null,
                iban_locked: false,
                swift_locked: false,
                can_edit: false,
            }
            return { ...base, ...this.bankOverride }
        },
        // `Order::items()` renvoie une liste PLATE (aucun filtre sur parent_id). On ne regroupe
        // QUE les billets d'une ligne conteneur simpleTicket, identifiée par le drapeau serveur
        // `is_container` (le front ne connaît pas la sous-catégorie de la prestation).
        //
        // Toute autre ligne reste rendue exactement comme avant, y compris une option produit
        // rattachée à une prestation payante : elle garde sa propre ligne, ses montants, sa
        // poignée de déplacement et sa corbeille.
        /**
         * Regroupe les lignes du devis en « ligne principale + lignes incluses ».
         *
         * Deux familles s'y rattachent, et elles ne se recouvrent pas :
         *  - conteneur simpleTicket : ses billets portent le montant (comportement d'origine) ;
         *  - RESTO : un plat porte ses options et suppléments, un menu porte ses produits —
         *    qui portent eux-mêmes leurs suppléments. Le devis n'affiche qu'un niveau
         *    d'imbrication : on rattache donc chaque ligne resto à sa ligne PRINCIPALE
         *    (`food_root_id`, calculé côté serveur), et non à son parent direct. Sans cela,
         *    le supplément d'un produit de menu remonterait en ligne principale du devis.
         *
         * Toute autre ligne (prestation, package, option produit) conserve exactement son
         * rendu antérieur.
         */
        itemGroups() {
            const containerIds = new Set(this.items.filter((item) => item.is_container && !item.parent_id).map((item) => item.id))
            // Lignes principales pouvant PORTER des options : tout ce qui n'est ni un conteneur
            // simpleTicket ni une prestation (produit boutique OU menu resto). Une option nichée
            // dessous s'y rattache ; sans cela elle remonterait en ligne principale à part.
            const optionParentIds = new Set(
                this.items
                    .filter((item) => !item.parent_id && !item.is_container && !this.isPrestationLine(item))
                    .map((item) => item.id)
            )

            // Identifiant du parent d'affichage d'une ligne RATTACHÉE (billet de conteneur, ligne
            // de menu resto, ou option), sinon null pour une ligne principale.
            const attachParentId = (item) => {
                if (item.parent_id && containerIds.has(item.parent_id)) return item.parent_id
                if (item.is_food_line && item.parent_id && item.food_root_id) return item.food_root_id
                if (item.parent_id && optionParentIds.has(item.parent_id)) return item.parent_id
                return null
            }

            const groups = []
            const groupIndexById = {}

            // Passe 1 : les lignes PRINCIPALES forment les groupes, dans leur ordre. Deux passes
            // (et non une) car une ligne enfant peut précéder son parent dans la liste — l'ordre
            // du tableau est celui du serveur, qui peut classer un enfant avant son parent sur
            // une donnée incohérente. En une passe, elle deviendrait une ligne principale isolée
            // (donc non éditable) au lieu de se nicher.
            this.items.forEach((item) => {
                if (attachParentId(item) !== null) return
                groupIndexById[item.id] = groups.length
                groups.push({ parent: item, children: [] })
            })

            // Passe 2 : chaque enfant rejoint son parent, quel que soit l'ordre des lignes.
            this.items.forEach((item) => {
                const parentId = attachParentId(item)
                if (parentId === null) return

                const at = groupIndexById[parentId]
                if (at !== undefined) {
                    groups[at].children.push(item)
                } else {
                    // Parent introuvable (donnée incohérente) : la ligne redevient autonome.
                    groupIndexById[item.id] = groups.length
                    groups.push({ parent: item, children: [] })
                }
            })

            return groups
        },
        /**
         * Codes promo groupés pour le popover : « Pourcentage » puis « Montant fixe ». L'ordre
         * des groupes est FIXE (celui de la maquette), pas celui de `promoCodes` ; un groupe vide
         * est retiré pour ne jamais afficher un en-tête orphelin. Un `discount_type` inconnu est
         * simplement ignoré plutôt que d'être rangé au hasard.
         */
        discountCodeGroups() {
            const byType = { percentage: [], fixed: [] }
            this.promoCodes.forEach((promo) => {
                const bucket = byType[String(promo.discount_type).toLowerCase()]
                if (bucket) bucket.push(promo)
            })
            return [
                { type: 'percentage', labelKey: 'devis.v3.discount_code_group_percentage', items: byType.percentage },
                { type: 'fixed', labelKey: 'devis.v3.discount_code_group_amount', items: byType.fixed },
            ].filter((group) => group.items.length > 0)
        },
        /**
         * Le code promo retenu, pour l'afficher dans la ligne « code appliqué ». `null` s'il n'est
         * plus proposé (désactivé, échu depuis l'enregistrement) : la ligne affiche alors le code
         * brut, sans valeur — on ne masque pas un code qui reste enregistré sur le devis.
         */
        selectedPromo() {
            return this.promoCodes.find((promo) => promo.code === this.discountCode) || null
        },
        /**
         * Nombre de lignes porteuses d'une remise propre (`remise_percent`), parents et enfants
         * confondus. Ne sert qu'au pluriel de la ligne « Remise(s) spécifique(s) ».
         */
        discountedItemCount() {
            return this.items.filter((item) => (Number(item.remise_percent) || 0) > 0).length
        },
    },
    methods: {
        normalizeFontSize,
        initSortable() {
            if (this.sortableInstance) {
                this.sortableInstance.destroy()
                this.sortableInstance = null
            }
            // Devis accepté : pas de réordonnancement (verrou édition).
            if (this.quoteLocked) return
            if (this.itemGroups.length <= 1) return
            const table = this.$el.querySelector('.devis-table')
            if (!table) return
            // Un <tbody> = une ligne principale + ses lignes incluses : on ne rend
            // déplaçables que les groupes, jamais une ligne enfant isolée.
            this.sortableInstance = Sortable.create(table, {
                animation: 150,
                draggable: 'tbody.item-group',
                handle: '.gripHandle',
                ghostClass: 'ppv2-bg-purple-100',
                onEnd: this.handleDragEnd,
            })
        },
        async handleDragEnd(evt) {
            if (this.quoteLocked) return
            // `oldIndex`/`newIndex` comptent TOUS les frères (le <thead> inclus) : avec un
            // `draggable` restreint aux tbody, seuls les index "draggable" sont fiables.
            const oldIndex = evt.oldDraggableIndex ?? evt.oldIndex
            const newIndex = evt.newDraggableIndex ?? evt.newIndex
            if (oldIndex == null || newIndex == null || oldIndex === newIndex) return
            if (this.isSavingPositions) {
                this.$toast?.info?.('Veuillez attendre la fin de la sauvegarde en cours...')
                return
            }
            this.isSavingPositions = true
            try {
                // On réordonne des GROUPES : chaque ligne incluse conserve la position
                // qui suit immédiatement sa ligne principale.
                const groups = [...this.itemGroups]
                const [moved] = groups.splice(oldIndex, 1)
                groups.splice(newIndex, 0, moved)
                const positions = []
                let ordre = 0
                groups.forEach((group) => {
                    positions.push({ id: group.parent.id, display_ordre: ++ordre })
                    group.children.forEach((child) => {
                        positions.push({ id: child.id, display_ordre: ++ordre })
                    })
                })
                const response = await axios.post('/back-office/v3/devis/reorder-items', {
                    order_id: this.order_id,
                    items: positions,
                })
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    // Le serveur a régénéré le snapshot du devis dans le nouvel ordre : on
                    // rafraîchit l'aperçu PDF (bump de version = cache-buster sur l'URL ?v=),
                    // sinon le navigateur ressert le PDF en cache et l'ordre semble inchangé.
                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }
                    this.$nextTick(() => this.initSortable())
                    this.$toast?.success?.(this.$t('devis.v3.order_updated'))
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Erreur lors du réordonnancement')
            } finally {
                this.isSavingPositions = false
            }
        },
        // Toute ligne PRINCIPALE se corrige (prestation, resto, menu, produit) ; une ligne
        // incluse suit sa mère. Miroir de la règle serveur (DevisStoreService::updateItemInQuote) :
        // le front n'autorise rien, il évite seulement de proposer une action refusée.
        isEditableLine(item) {
            return !item.parent_id
        },
        // La quantité d'une prestation est un nombre de PARTICIPANTS, contrôlé contre la capacité
        // du créneau : elle se choisit dans la liste du catalogue. Partout ailleurs c'est une
        // quantité de ligne, saisie librement.
        isPrestationLine(item) {
            return String(item.item_type || '').includes('Prestation')
        },
        // Réservation « ESPACE ENTIER » d'une prestation (type booking) : la capacité est prise en
        // totalité, donc le serveur stocke la ligne comme UNE réservation de groupe (quantity = 1) au
        // PRIX DU GROUPE, tandis que nb_contributor porte le nombre réel de participants. Sans repère,
        // l'utilisateur voit « 1 » en quantité et le prix du groupe en PU et croit à un bug.
        // Reconnu sans ambiguïté — et UNIQUEMENT ce cas : prestation, quantité stockée 1, participants
        // > 1 (une billetterie, paintball inclus, a quantity = participants, donc n'entre jamais ici).
        isWholeSpaceBooking(item) {
            return this.isPrestationLine(item) && Number(item.quantity) === 1 && Number(item.nb_contributor) > 1
        },
        // Prix PAR PLACE indicatif d'une réservation espace entier : prix du groupe ÷ participants,
        // dans le mode d'affichage courant (HT/TTC). Purement informatif — n'altère ni le PU stocké ni
        // le total de la ligne (qui restent le prix de groupe × 1).
        perPlaceUnitPrice(item) {
            const participants = Number(item.nb_contributor) || 1
            const groupUnit = this.priceDisplayMode === 'ht' ? Number(item.price_ht) || 0 : Number(item.price) || 0
            return participants > 0 ? groupUnit / participants : groupUnit
        },
        // Ligne PRODUIT (boutique ou ligne personnalisée : toutes deux item_type = Product).
        isProductLine(item) {
            return String(item.item_type || '').includes('Product')
        },
        // Une ligne incluse MODIFIABLE (désignation + prix) : toute ligne de composition —
        // option produit, ou ligne d'un menu resto — dont ni elle ni son parent ne relèvent
        // d'une prestation. Les billets/participants d'une prestation (et d'un conteneur
        // simpleTicket) restent dérivés de leur mère. Miroir exact de updateItemInQuote.
        isEditableChild(child, group) {
            if (!group || !child.parent_id) return false
            if (this.isPrestationLine(child) || this.isPrestationLine(group.parent) || group.parent.is_container) {
                return false
            }
            return true
        },
        // Une ligne principale peut PORTER des options (produit boutique ou menu resto) : tout
        // sauf une prestation ou un conteneur simpleTicket. Miroir de la règle serveur
        // (DevisProductItemService::addCustomLine).
        canAddOption(item) {
            if (this.quoteLocked) return false
            return this.isEditableLine(item) && !item.is_container && !this.isPrestationLine(item)
        },
        // La TVA d'une ligne n'est ajustable que là où le serveur la fait tenir : les lignes dont
        // l'article est un PRODUIT TECHNIQUE du devis (ligne personnalisée ou option,
        // created_from_devis). Ailleurs — produit catalogue, composant de menu, prestation — le
        // taux suit l'article (SyncTvaOrder le redérive), et l'ajuster par ligne serait sans effet.
        canEditVat(item) {
            if (this.quoteLocked) return false
            return !!(item && item.itemable && item.itemable.created_from_devis) && !item.is_container
        },
        // Options du sélecteur de TVA, au format attendu par BaseSelect ({ value, label }) : le taux
        // courant est toujours présent, même hors barème, sinon une ligne à 8 % perdrait sa valeur
        // dans la liste. Le libellé est formaté ici (la fr-FR) puisque le menu n'a plus de gabarit
        // pour le faire.
        vatRateOptions(item) {
            const rates = [0, 5.5, 10, 20]
            const currentRate = Number(item?.vat_rate)
            if (Number.isFinite(currentRate) && !rates.includes(currentRate)) rates.push(currentRate)
            return rates.sort((a, b) => a - b).map((rate) => ({ value: rate, label: `${this.formatVatRate(rate)} %` }))
        },
        formatVatRate(rate) {
            return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(Number(rate) || 0)
        },
        // Choisit le taux puis enregistre la case, comme le blur/l'Entrée le faisaient sur l'ancien
        // <input>. BaseSelect n'émet `input` que sur un CHANGEMENT de valeur : le garde protège
        // d'un second enregistrement du même taux si l'événement remontait malgré tout.
        selectVatRate(rate) {
            if (this.cellEdit.saving) return
            const value = Number(rate)
            if (Number.isNaN(value) || value === Number(this.cellEdit.value)) return
            this.cellEdit.value = value
            this.saveCellEdit()
        },
        // Total NET d'une ligne : prix × quantité, moins la remise de ligne (en %). C'est ce que
        // le total du devis retient (remise_price) — la cellule doit donc l'afficher, pas le brut.
        lineTotal(item) {
            const gross = (Number(item.quantity) || 0) * (Number(item.price) || 0)
            const remise = Number(item.remise_percent) || 0
            return gross * (1 - remise / 100)
        },
        // Total HT NET d'une ligne : PU HT × quantité, moins la remise de ligne (en %). Pur affichage,
        // même remise que lineTotal ; aucune valeur n'est renvoyée au serveur.
        lineTotalHt(item) {
            const gross = (Number(item.quantity) || 0) * (Number(item.price_ht) || 0)
            const remise = Number(item.remise_percent) || 0
            return gross * (1 - remise / 100)
        },
        /**
         * Une ligne dont la quantité est un nombre LIBRE (produit, ligne personnalisée, vente
         * additionnelle, resto…) : elle reçoit le stepper direct − / qté / +. Exclues : les
         * conteneurs (quantité = somme des billets) et les prestations à créneau (quantité =
         * participants, bornée par la capacité — elle garde son sélecteur dédié).
         */
        hasQtyStepper(item) {
            if (this.quoteLocked) return false
            return this.isEditableLine(item) && !item.is_container && !(this.usesTimeSlots && this.isPrestationLine(item))
        },
        /**
         * La quantité est-elle modifiable sur CETTE ligne, maintenant ?
         *
         * Une seule règle pour les quatre endroits qui doivent s'y conformer : le `disabled` des
         * deux steppers, `startCellEdit` et `stepQuantity`. Tant qu'une ligne est en édition de
         * ligne, sa quantité est verrouillée — SAUF si c'est la ligne en CAPTURE de désignation :
         * la maquette y laisse le stepper actif, et verrouiller ici reviendrait à interdire de
         * fixer une quantité à une ligne dont on n'a même pas encore le nom.
         *
         * Une option n'est jamais en capture, donc pour elle la condition se réduit à l'ancien
         * verrou : rien ne change, mais les deux surfaces partagent désormais la même règle au
         * lieu de la répéter.
         */
        canTouchQty(line) {
            if (!line) return false
            if (this.quoteLocked) return false

            return this.qtyUpdatingId === null && (this.editingItemId === null || this.customLineCaptureId === line.id)
        },
        /**
         * Corbeille du stepper : deux sémantiques distinctes selon l'état de la ligne.
         *
         * Pendant la CAPTURE, retirer la ligne = abandonner le brouillon en cours de saisie :
         * même sens qu'Échap, donc `cancelCapture`, et surtout PAS de modale — on n'a pas à
         * confirmer la suppression d'une ligne que l'utilisateur est en train de composer.
         * Hors capture, c'est le retrait normal, qui garde la confirmation et le garde-fou
         * « dernier article » de `handleRemoveItem`.
         */
        onQtyRemove(line, group = null) {
            if (this.quoteLocked) return
            if (this.customLineCaptureId === line.id) {
                this.cancelCapture()
                return
            }

            this.handleRemoveItem(line.id, group)
        },
        /**
         * Même contrôle sur une ligne INCLUSE (option produit, supplément de menu). On délègue
         * entièrement à `isEditableChild` : c'est le miroir de `updateItemInQuote`, donc le serveur
         * et l'UI ne peuvent pas diverger sur ce qui est modifiable. Un enfant n'est jamais un
         * conteneur et n'a pas de quantité-participants, donc aucune exception supplémentaire à écrire.
         */
        hasChildQtyStepper(child, group) {
            if (this.quoteLocked) return false
            return this.isEditableChild(child, group)
        },
        /** Cette case précise est-elle en cours d'édition ? */
        isCellEditing(item, field) {
            return this.cellEdit.id === item.id && this.cellEdit.field === field
        },
        /**
         * QtyStepper est un composant CONTRÔLÉ : il émet `input` mais ne détient pas la valeur.
         * On la recopie dans `cellEdit`, que `saveCellEdit` lit ensuite pour monter le payload.
         */
        setQtyCellDraft(value) {
            this.cellEdit = { ...this.cellEdit, value }
        },
        canEditTotal(item, group = null) {
            if (this.quoteLocked) return false
            if (!item || item.is_container || (Number(item.remise_percent) || 0) >= 100) return false
            return item.parent_id ? this.isEditableChild(item, group) : this.isEditableLine(item)
        },
        /**
         * Ouvre l'édition d'UNE case (crayon au survol). `custom_label` reprend le libellé
         * affiché (custom_label sinon nom de l'article) ; `price_ht` reprend le P.U. HT courant.
         * Bloqué si une autre écriture est en cours ou si la ligne est déjà en édition complète.
         */
        startCellEdit(item, field) {
            // Devis accepté : édition verrouillée (la bascule Option reste la seule exception).
            if (this.quoteLocked) return
            if (this.cellEdit.saving || !this.canTouchQty(item)) {
                return
            }

            let value
            if (field === 'custom_label') {
                value = item.more_details?.custom_label || (item.itemable ? item.itemable.name : '')
            } else if (field === 'quantity') {
                value = item.quantity
            } else if (field === 'vat_rate') {
                value = item.vat_rate
            } else if (field === 'remise_percent') {
                value = item.remise_percent || 0
            } else if (field === 'total') {
                value = this.lineTotal(item)
            } else {
                value = item.price_ht
            }

            this.cellEdit = { id: item.id, field, value, saving: false }
            this.$nextTick(() => {
                if (field === 'vat_rate') {
                    const vatSelect = this.$refs['vatCellSelect_' + item.id]
                    const select = Array.isArray(vatSelect) ? vatSelect[0] : vatSelect
                    select?.openDropdown()
                    return
                }

                const el = this.$refs['cellEditInput_' + item.id]
                const input = Array.isArray(el) ? el[0] : el
                if (input) {
                    input.focus()
                    if (typeof input.select === 'function') input.select()
                }
            })
        },
        cancelCellEdit() {
            this.cellEdit = { id: null, field: null, value: '', saving: false }
        },
        /**
         * Enregistre la case éditée — et ELLE SEULE. Passe par update-item avec le seul champ
         * concerné : le serveur recalcule les totaux (HT/TVA/TTC) et régénère le devis, comme
         * l'édition de ligne. Pour le P.U. HT, on envoie aussi `price` (TTC) dérivé du taux,
         * comme le fait saveEditing.
         */
        async saveCellEdit() {
            if (this.cellEdit.saving || this.cellEdit.id === null) return

            const line = (this.items || []).find((it) => it.id === this.cellEdit.id)
            const field = this.cellEdit.field
            if (!line) {
                this.cancelCellEdit()
                return
            }

            const payload = { order_id: this.order_id, order_item_id: this.cellEdit.id }

            if (field === 'total') {
                const total = Number(this.cellEdit.value)
                const quantity = Number(line.quantity) || 0
                const remise = Number(line.remise_percent) || 0
                const quantityAfterDiscount = quantity * (1 - remise / 100)
                if (!Number.isFinite(total) || total < 0 || quantityAfterDiscount <= 0) {
                    this.$toast.error(this.$t('devis.v3.edit_total_error'))
                    return
                }

                const unitPrice = total / quantityAfterDiscount
                const vatRate = Number(line.vat_rate) || 0
                payload.price = unitPrice
                payload.price_ht = unitPrice / (1 + vatRate / 100)
            } else if (field === 'price_ht') {
                const priceHt = Number(this.cellEdit.value)
                if (!Number.isFinite(priceHt) || priceHt < 0) {
                    this.$toast.error('Le prix ne peut pas être négatif.')
                    return
                }
                payload.price_ht = priceHt
                payload.price = priceHt * (1 + Number(line.vat_rate || 0) / 100)
            } else if (field === 'quantity') {
                // Quantité entière ≥ 1 : même plancher que le stepper (le « − » y devient
                // corbeille à 1). Un champ vidé ou < 1 est ramené à 1, jamais 0.
                const qty = parseInt(this.cellEdit.value, 10)
                payload.quantity = Number.isFinite(qty) && qty >= 1 ? qty : 1
            } else if (field === 'vat_rate') {
                // Taux de TVA borné à [0, 100]. Le serveur redérive le TTC du HT et du taux :
                // on n'envoie donc PAS de price ici.
                const vat = Number(this.cellEdit.value)
                if (!Number.isFinite(vat) || vat < 0 || vat > 100) {
                    this.$toast.error('Le taux de TVA doit être compris entre 0 et 100.')
                    return
                }
                payload.vat_rate = vat
            } else if (field === 'remise_percent') {
                // Remise en pourcentage, bornée à [0, 100].
                const remise = Number(this.cellEdit.value)
                if (!Number.isFinite(remise) || remise < 0 || remise > 100) {
                    this.$toast.error('La remise doit être comprise entre 0 et 100 %.')
                    return
                }
                payload.remise_percent = remise
            } else {
                payload.custom_label = this.cellEdit.value
            }

            this.cellEdit.saving = true
            try {
                const response = await axios.post('/back-office/v3/devis/update-item', payload)
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Erreur lors de la modification')
            } finally {
                this.cancelCellEdit()
            }
        },
        /**
         * Incrémente / décrémente la quantité d'une ligne d'UN, directement (sans passer par le
         * mode édition). Réutilise l'endpoint update-item : le serveur recalcule les totaux
         * (HT / TVA / TTC) et régénère le devis, exactement comme l'édition inline.
         *
         * La quantité ne descend jamais sous 1 : à 1, le « − » laisse place à la corbeille
         * (handleRemoveItem), c'est le seul chemin pour retirer la ligne depuis le stepper.
         */
        async stepQuantity(line, delta) {
            if (this.quoteLocked) return
            const newQty = Number(line.quantity) + delta

            if (newQty < 1 || !this.canTouchQty(line)) {
                return
            }

            this.qtyUpdatingId = line.id
            try {
                const response = await axios.post('/back-office/v3/devis/update-item', {
                    order_id: this.order_id,
                    order_item_id: line.id,
                    quantity: newQty,
                })

                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)

                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Erreur lors de la modification')
            } finally {
                this.qtyUpdatingId = null
            }
        },
        startEditing(item) {
            if (this.quoteLocked) return
            // Une seule ligne en édition à la fois : ouvrir une autre ligne abandonne les
            // modifications non enregistrées de la précédente (rien n'a été envoyé au serveur).
            if (this.editingItemId && this.editingItemId !== item.id) {
                this.cancelEditing()
            }

            this.editingItemId = item.id
            this.editForm = {
                custom_label: item.more_details?.custom_label || (item.itemable ? item.itemable.name : ''),
                quantity: item.quantity,
                price_ht: item.price_ht,
                vat_rate: item.vat_rate,
                // `is_container` est calculé côté serveur (DevisController::flagContainerItems).
                can_edit_amounts: !item.is_container,
                is_optional: !!item.is_optional,
            }
            this.loadEditQuantityOptions(item)
        },
        // Le sélecteur de participants reprend la source de l'étape Participants du stepper :
        // possible_tickets sinon l'intervalle min_contributors → max_contributors du catalogue.
        // Repli sur la quantité courante si les détails ne sont pas récupérables, pour ne jamais
        // présenter un select vide.
        async loadEditQuantityOptions(item) {
            const current = Number(item.quantity) || 1
            this.editQuantityOptions = [{ value: current, label: this.participantsLabel(current) }]

            // Sans créneau — ou hors prestation, où la quantité n'est pas un nombre de
            // participants — la saisie est libre : aucune liste à construire.
            if (!this.usesTimeSlots || !item.item_id || !this.isPrestationLine(item)) return

            this.loadingEditOptions = true
            try {
                const { data } = await axios.get(`/back-office/v3/devis/items/${item.item_id}/details?type=experience`)

                let values = []
                if (data?.possible_tickets?.length > 0) {
                    values = data.possible_tickets
                } else {
                    const min = parseInt(data?.min_contributors) || 1
                    const max = Math.min(parseInt(data?.max_contributors) || 50, 50)
                    for (let i = min; i <= max; i++) values.push(i)
                }

                // La quantité en place doit toujours exister dans la liste, même si le catalogue
                // a changé depuis la création de la ligne.
                if (!values.includes(current)) values.push(current)
                values.sort((a, b) => a - b)

                this.editQuantityOptions = values.map((n) => ({ value: n, label: this.participantsLabel(n) }))
            } catch (error) {
                console.error('Impossible de charger les participants possibles :', error)
            } finally {
                this.loadingEditOptions = false
            }
        },
        participantsLabel(n) {
            return `${n} ${n > 1 ? 'personnes' : 'personne'}`
        },
        // Ouvre la popup de planning sur le créneau courant de la ligne. Les champs que la
        // popup ne trouve pas sur l'OrderItem (espace, durée) viennent du backend via
        // formatOrderResponse ; sans eux, StepPlanning rechargerait une mauvaise formule.
        openSlotEditor(item) {
            this.editingReservationItem = {
                id: item.id,
                item_id: item.item_id,
                slug: item.itemable?.slug || null,
                price: item.price,
                quantity: item.quantity,
                nb_contributor: item.nb_contributor,
                start_time: item.start_time,
                end_time: item.end_time,
                space_id: item.space_id ?? item.time_slot?.id_space ?? null,
                duration_id: item.duration_id ?? null,
                authorize_mixage: item.authorize_mixage ?? item.time_slot?.authorize_mixage ?? false,
                ticket_selections: this.buildTicketSelections(item),
            }
            this.$nextTick(() => {
                this.showSlotEditor = true
            })
        },
        // Billets d'un conteneur simpleTicket, dans la forme attendue par le stepper
        // ({ prestationId: quantité }) : la popup doit rouvrir avec la même sélection.
        buildTicketSelections(item) {
            const children = this.items.filter((i) => i.parent_id === item.id)
            return children.reduce((acc, child) => {
                acc[child.item_id] = child.quantity
                return acc
            }, {})
        },
        async handleUpdateItemReservation({ order_item_id, item }) {
            try {
                const response = await axios.post('/back-office/v3/devis/update-item-reservation', {
                    order_id: this.order_id,
                    order_item_id,
                    reservation: {
                        prestationSlug: item.prestationSlug,
                        space: item.space,
                        reservationType: item.reservationType,
                        authorize_mixage: item.authorize_mixage,
                    },
                })
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    this.$toast.success('Créneau modifié avec succès')
                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Erreur lors de la modification du créneau')
            }
        },
        cancelEditing() {
            // Ligne personnalisée à peine créée et jamais validée : l'annulation l'ABANDONNE,
            // donc on la retire (elle n'existe en base que pour être renseignée ici). Le retrait
            // rafraîchit déjà les lignes ; on sort avant la remise à zéro locale qu'il fait aussi.
            const draftId = this.justCreatedCustomLineId
            this.justCreatedCustomLineId = null
            this.customLineCaptureId = null
            this.captureSaving = false

            if (draftId && this.editingItemId === draftId) {
                this.editingItemId = null
                this.editQuantityOptions = []
                this.handleRemoveItem(draftId)
                return
            }

            this.editingItemId = null
            this.editQuantityOptions = []
        },
        /**
         * Capture auto-save : enregistre uniquement la désignation (qty=1, price=0 déjà côté
         * serveur). Appelé sur Entrée, blur ou après un Escape (qui annule au lieu de sauver).
         */
        autoSaveCapture() {
            if (this.captureSaving) return
            this.captureSaving = true

            const captureId = this.customLineCaptureId
            if (!captureId) {
                this.captureSaving = false
                return
            }

            const label = (this.editForm.custom_label || '').trim()
            if (!label) {
                this.cancelCapture()
                return
            }

            this.savingEdit = true
            const payload = {
                order_id: this.order_id,
                order_item_id: captureId,
                custom_label: label,
                is_optional: !!this.editForm.is_optional,
            }

            axios
                .post('/back-office/v3/devis/update-item', payload)
                .then((response) => {
                    if (response.data.success) {
                        this.items = response.data.order.items
                        this.updateTotalsFromOrder(response.data.order)
                        this.$toast.success(this.$t('devis.v3.item_modified'))

                        if (this.$refs.devisPopup) {
                            this.$refs.devisPopup.version++
                            this.$refs.devisPopup.buildDocumentURL()
                        }
                    }
                })
                .catch((error) => {
                    this.$toast.error(error.response?.data?.message || this.$t('devis.v3.error_editing'))
                })
                .finally(() => {
                    this.editingItemId = null
                    this.customLineCaptureId = null
                    this.captureSaving = false
                    this.savingEdit = false
                    this.justCreatedCustomLineId = null
                    this.editQuantityOptions = []
                })
        },
        /**
         * Annule le mode capture : ferme l'édition SANS sauvegarder.
         * Si c'est un brouillon personnalisé, la ligne est supprimée.
         */
        cancelCapture() {
            const draftId = this.justCreatedCustomLineId
            this.justCreatedCustomLineId = null
            this.customLineCaptureId = null
            this.captureSaving = false

            if (draftId && this.editingItemId === draftId) {
                this.editingItemId = null
                this.editQuantityOptions = []
                this.handleRemoveItem(draftId)
                return
            }

            this.editingItemId = null
            this.editQuantityOptions = []
        },
        async saveEditing() {
            if (!this.editingItemId) return
            this.savingEdit = true
            try {
                // Un conteneur simpleTicket est à 0 € et sa quantité est la somme de ses billets :
                // le serveur refuse d'y toucher, on n'envoie donc que le libellé.
                const payload = {
                    order_id: this.order_id,
                    order_item_id: this.editingItemId,
                    custom_label: this.editForm.custom_label,
                    is_optional: !!this.editForm.is_optional,
                }

                if (this.editForm.can_edit_amounts) {
                    // Un prix à 0 est légitime (ligne offerte, conteneur) ; un prix négatif ne
                    // l'est jamais. Contrôlé ici pour un retour immédiat, et refusé côté serveur.
                    // Number.isFinite couvre aussi le champ vidé, qui partait sinon en NaN.
                    const priceHt = Number(this.editForm.price_ht)
                    if (!Number.isFinite(priceHt) || priceHt < 0) {
                        this.$toast.error('Le prix ne peut pas être négatif.')
                        this.savingEdit = false
                        return
                    }

                    payload.quantity = this.editForm.quantity
                    payload.price_ht = priceHt
                    payload.price = priceHt * (1 + this.editForm.vat_rate / 100)
                }

                const response = await axios.post('/back-office/v3/devis/update-item', payload)
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    this.editingItemId = null
                    // Une fois validée, la ligne personnalisée n'est plus un brouillon : annuler
                    // une future édition ne doit plus la supprimer.
                    this.justCreatedCustomLineId = null
                    this.$toast.success('Ligne modifiée avec succès')

                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Erreur lors de la modification')
            } finally {
                this.savingEdit = false
            }
        },
        // Clic dans la case « Option » pendant une édition de ligne / capture / sauvegarde de
        // cellule : on bloque le mousedown pour ne pas voler le focus de l'input en cours (son
        // blur validerait ou supprimerait un brouillon).
        onOptionCellMousedown(event) {
            if (this.editingItemId !== null || this.customLineCaptureId !== null || this.cellEdit.saving) {
                event.preventDefault()
            }
        },
        // Bascule « ligne proposée en option » (is_optional). La ligne reste au devis mais le
        // SERVEUR l'exclut du total et renvoie l'ordre recalculé : on ne recompute RIEN ici, on
        // réécrit l'état renvoyé. Un seul envoi à la fois ; en cas de refus/erreur on rétablit
        // l'état serveur de la case.
        async toggleOptional(parent, event) {
            const desired = event.target.checked
            if (this.optionUpdatingId !== null || this.editingItemId !== null || this.cellEdit.saving) {
                event.target.checked = !!parent.is_optional
                return
            }
            this.optionUpdatingId = parent.id
            try {
                const response = await axios.post('/back-office/v3/devis/update-item', {
                    order_id: this.order_id,
                    order_item_id: parent.id,
                    is_optional: desired,
                })
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)

                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }
                } else {
                    event.target.checked = !!parent.is_optional
                    this.$toast.error(this.$t('devis.v3.error_editing'))
                }
            } catch (error) {
                event.target.checked = !!parent.is_optional
                this.$toast.error(error.response?.data?.message || this.$t('devis.v3.error_editing'))
            } finally {
                this.optionUpdatingId = null
            }
        },
        async getTemplates() {
            try {
                const res = await ApiManager.getAllQuotesTemplates()
                this.templates = Array.isArray(res.data) ? res.data : []
            } catch (error) {
                console.error('Error fetching templates:', error)
            }
        },
        async getAppliedQuote() {
            if (!this.order_id) return
            try {
                const res = await ApiManager.getQuote(this.order_id)
                if (res.data.quote) {
                    this.form.template_id = res.data.quote.template_id ?? -1
                }
            } catch (error) {
                console.error('Error fetching quote details:', error)
            }
        },
        async saveTemplate() {
            if (this.quoteLocked) return
            if (!this.order_id) return
            try {
                await ApiManager.saveQuoteTemplate(this.order_id, this.form.template_id == -1 ? null : this.form.template_id)
                this.$toast.success(this.$t('devis.v3.template_updated'))
                // Refresh PDF if needed
                if (this.$refs.devisPopup) {
                    this.$refs.devisPopup.version++
                    this.$refs.devisPopup.buildDocumentURL()
                }
            } catch (error) {
                this.$toast.error(this.$t('devis.v3.error_template'))
            }
        },
        handleBack() {
            window.location.href = '/back-office/devis'
        },
        /**
         * « Enregistrer » : on RESTE sur le devis.
         *
         * La redirection vers la liste faisait perdre le contexte à chaque enregistrement,
         * alors que la composition d'un devis est itérative. Retourner à la liste reste
         * possible, c'est le bouton « Retour » (`handleBack`) qui le fait.
         *
         * Rien d'autre ne change : l'enregistrement lui-même est toujours `persistQuote`.
         */
        async handleSave() {
            // Devis accepté : rien à enregistrer (seule la case Option reste modifiable, et elle se
            // sauvegarde seule). On neutralise « Enregistrer » pour éviter un 422 serveur inutile.
            if (this.quoteLocked) return
            if (!(await this.persistQuote())) return

            this.$toast.success(this.$t('devis.v3.finalized_success'))
        },
        /**
         * Enregistre le devis (POST /finalize) et dit si la base est à jour.
         *
         * Extrait de handleSave() pour être réutilisé AVANT un envoi : le client choisi à
         * l'écran n'est rattaché à la commande que par finalize (Order::user_id +
         * syncOrderClient). Sans cet enregistrement préalable, le serveur envoie le devis
         * au « client de passage » pré-sélectionné à l'ouverture — qui n'a pas d'adresse
         * email — et l'envoi échoue alors que l'écran affiche bien un client.
         *
         * C'est aussi finalize qui régénère le snapshot du devis (InvoiceService::makeQuote) :
         * le PDF joint au mail correspond donc à ce qui est affiché à l'écran.
         */
        async persistQuote() {
            // Le RIB est enregistré dans les Paramètres de l'établissement même si le
            // devis n'est pas encore finalisable (pas de client / pas de ligne) : c'est
            // une donnée d'établissement, pas une donnée du devis.
            if (!(await this.persistBankDraft())) return false

            if (!this.selectedClient) {
                this.$toast.error(this.$t('devis.v3.select_client_first'))
                return false
            }
            if (!this.order_id) {
                this.$toast.error(this.$t('devis.v3.add_at_least_reservation'))
                return false
            }
            this.saving = true
            try {
                // Multipart : le logo propre au devis peut être un nouveau fichier.
                const formData = new FormData()
                formData.append('order_id', this.order_id)
                formData.append('client_id', this.selectedClient.id)
                formData.append('note', this.note || '')
                formData.append('client_request', this.clientRequest || '')
                if (this.form.due_date) formData.append('due_date', this.form.due_date)
                if (this.form.template_id != null) formData.append('template_id', this.form.template_id)
                if (this.form.logo instanceof File) {
                    formData.append('logo', this.form.logo)
                } else if (!this.form.logo && this.initialLogo && !this.logoFromEstablishment) {
                    // Logo existant retiré par l'utilisateur.
                    formData.append('logo_removed', '1')
                }
                const response = await axios.post('/back-office/v3/devis/finalize', formData)

                return response.data.success === true
            } catch (error) {
                console.error('Error finalizing quote:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_finalizing'))

                return false
            } finally {
                this.saving = false
            }
        },
        // L'échéance est une donnée du devis : elle doit survivre à un rechargement sans
        // attendre l'ajout d'une ligne ou l'enregistrement final — les deux seuls chemins
        // qui la transportaient, d'où une valeur par défaut qui « revenait » après F5.
        // Sur un devis pas encore créé (aucun order_id), elle part avec le premier article.
        async persistDueDate(previousValidity, previousDueDate) {
            if (!this.order_id) return
            try {
                await axios.post('/back-office/v3/devis/due-date', {
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                })
            } catch (error) {
                // Refus serveur (échéance sous la dernière prestation, course avec un autre
                // onglet) : on restaure l'état enregistré plutôt que d'afficher une échéance
                // que la base ne porte pas.
                this.$toast.warning(error.response?.data?.message ?? this.$t('devis.v3.error_due_date'), { timeout: 6000 })
                this.skipValidityWatch = true
                this.form.validity = previousValidity
                this.form.due_date = previousDueDate
            }
        },
        // Regroupe les bascules successives en un seul enregistrement.
        schedulePaymentMethodsSave() {
            if (this.paymentsSaveTimer) clearTimeout(this.paymentsSaveTimer)
            this.paymentsSaveTimer = setTimeout(() => this.persistPaymentMethods(), 500)
        },
        /**
         * Enregistre les moyens de paiement proposés sur le devis.
         *
         * Même principe que l'échéance : la donnée appartient au devis et doit survivre à un
         * rechargement. Tant que le devis n'existe pas (aucun order_id), la sélection reste
         * locale et part dès sa création (watcher `order_id`).
         *
         * ⚠️ Zéro confiance : on n'envoie que des identifiants ; le serveur les restreint aux
         * moyens réellement disponibles sur l'établissement.
         */
        async persistPaymentMethods() {
            if (!this.order_id) return

            const ids = Object.keys(this.form.payments)
                .filter((id) => this.form.payments[id])
                .map(Number)

            try {
                await axios.post('/back-office/v3/devis/payment-methods', {
                    order_id: this.order_id,
                    payment_method_ids: ids,
                })
            } catch (error) {
                // Échec d'enregistrement : on le dit plutôt que de laisser croire à une
                // sélection persistée (elle disparaîtrait au rechargement suivant).
                this.$toast.warning(error.response?.data?.message ?? this.$t('devis.v3.error_payment_methods'), {
                    timeout: 6000,
                })
            }
        },
        /**
         * Enregistre le délai de relance automatique choisi sur le devis (7 / 3 / 1 jour
         * avant échéance).
         *
         * Même principe que l'échéance et les moyens de paiement : la donnée appartient au
         * devis et doit survivre à un rechargement. Tant que le devis n'existe pas (aucun
         * order_id), le choix reste local et part dès sa création (watcher `order_id`).
         *
         * ⚠️ Zéro confiance : le serveur restreint la valeur à sa propre liste blanche
         * (DevisAutoRelaunchService::ALLOWED_DAYS) — ce que l'écran propose ne l'engage pas.
         *
         * @param {number|null} previousDays valeur à restaurer si le serveur refuse
         */
        async persistAutoRelaunchDays(previousDays) {
            if (!this.order_id) return

            try {
                await axios.post('/back-office/v3/devis/auto-relaunch-days', {
                    order_id: this.order_id,
                    auto_relaunch_days: this.form.auto_reminder,
                })
            } catch (error) {
                // On restaure la valeur enregistrée plutôt que d'afficher un délai que la
                // base ne porte pas : l'écran annoncerait une relance qui n'aurait pas lieu.
                this.$toast.warning(error.response?.data?.message ?? this.$t('devis.v3.error_auto_reminder'), {
                    timeout: 6000,
                })
                if (previousDays !== null && previousDays !== undefined) {
                    this.skipAutoReminderWatch = true
                    this.form.auto_reminder = previousDays
                }
            }
        },
        clearBankError(field) {
            this.$set(this.bankErrors, field, null)
        },
        updateBankDraft({ field, value }) {
            this.$set(this.bankDraft, field, value)
        },
        // Première saisie du RIB depuis le devis : elle est enregistrée dans les
        // Paramètres de l'établissement. Le serveur refuse toute écrasure d'une valeur
        // existante et renvoie le RIB à jour, qui verrouille alors le champ.
        // Retourne false si la saisie a été refusée (format invalide) → on n'enchaîne pas.
        async persistBankDraft() {
            const payload = {}
            // Seuls les champs encore vides côté Paramètres sont envoyés (première saisie).
            ;['iban', 'swift'].forEach((field) => {
                const value = (this.bankDraft[field] || '').trim()
                if (value && !this.bank[field]) payload[field] = value
            })

            if (!Object.keys(payload).length || this.savingBank) return true

            this.savingBank = true
            Object.keys(payload).forEach((field) => this.clearBankError(field))
            try {
                const response = await axios.post('/back-office/v3/devis/establishment-bank', payload)
                this.bankOverride = { ...this.bankOverride, ...(response.data?.bank || {}) }
                this.$toast.success(this.$t('devis.v3.bank_saved_success'))
                return true
            } catch (error) {
                const errors = error.response?.data?.errors || {}
                Object.keys(payload).forEach((field) => {
                    if (errors[field]?.[0]) this.$set(this.bankErrors, field, errors[field][0])
                })
                const message = error.response?.data?.message ?? this.$t('devis.v3.bank_save_error')
                this.$toast.error(message)
                return false
            } finally {
                this.savingBank = false
            }
        },
        /**
         * Clic sur « Envoyer » : on n'envoie pas tout de suite, on propose les deux modes.
         * Les conditions d'envoi sont vérifiées AVANT d'ouvrir le menu — inutile de laisser
         * choisir un mode d'envoi sur un devis qui n'a ni ligne ni client.
         */
        handleSend() {
            if (this.quoteLocked) return
            if (!this.canSendQuote()) return
            this.showSendDropdown = !this.showSendDropdown
        },
        /**
         * Un devis n'est envoyable qu'une fois créé côté serveur (order_id) et rattaché à un
         * VRAI client : un client de passage n'a pas d'adresse à qui écrire. Le serveur refuse
         * de toute façon (DevisController::sentQuote) — on évite juste l'aller-retour.
         */
        canSendQuote() {
            if (!this.order_id) {
                this.$toast.error(this.$t('devis.v3.no_quote_generated'))
                return false
            }
            if (!this.selectedClient || this.isPassageClient) {
                this.$toast.error(this.$t('devis.v3.select_client_first'))
                return false
            }
            return true
        },
        // Envoi simple : le mail de devis standard, exactement celui du listing et du
        // drag & drop kanban. Aucun contenu rédigé, rien à saisir.
        async handleSimpleSend() {
            this.showSendDropdown = false
            if (this.quoteLocked || this.sendingQuote || !this.canSendQuote()) return

            this.sendingQuote = true
            try {
                // On enregistre AVANT d'envoyer : sinon le serveur travaille sur l'état
                // précédent de la commande (client de passage, lignes ou échéance périmées)
                // et le client reçoit un devis qui n'est pas celui affiché à l'écran.
                if (!(await this.persistQuote())) return

                await axios.get(`/back-office/devis/${this.order_id}/sent-quote`)
                this.$toast.success(this.$t('devis.v3.sent_success'))
            } catch (error) {
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.sent_error'))
            } finally {
                this.sendingQuote = false
            }
        },
        // Envoi personnalisé : la popup rédige le corps du mail, l'envoi part de là.
        // Même enregistrement préalable que l'envoi simple — la popup n'ouvre que sur un
        // devis à jour, donc sur le bon destinataire et le bon PDF.
        async handleCustomSend() {
            this.showSendDropdown = false
            if (this.quoteLocked || this.sendingQuote || !this.canSendQuote()) return

            this.sendingQuote = true
            try {
                if (!(await this.persistQuote())) return

                this.showSendModal = true
            } finally {
                this.sendingQuote = false
            }
        },
        handleClickOutsideSend(event) {
            if (this.$refs.sendDropdown && !this.$refs.sendDropdown.contains(event.target)) {
                this.showSendDropdown = false
            }
        },
        handlePreviewPDF() {
            // Le devis (DevisInvoice) est généré côté serveur dès qu'un article est ajouté,
            // mais il n'expose pas d'URL PDF directe : on cible la route de rendu makeDevis
            // (la même que celle utilisée par l'iframe de QuoteModal).
            if (this.order_id) {
                window.open(`/back-office/devis/${this.order_id}`, '_blank')
            } else {
                this.$toast.error(this.$t('devis.v3.no_quote_generated'))
            }
        },
        openClientSelector() {
            this.clientPrefillName = ''
            this.clientEditId = null
            this.changingClient = true
            this.showClientSelector = true
        },
        changeClient() {
            this.changingClient = true
            this.$nextTick(() => this.$refs.clientSearch?.$refs.input?.focus())
        },
        /**
         * « Modifier la fiche client » : le popup s'ouvre sur le client rattaché au devis.
         *
         * Un client de passage n'a pas de fiche à modifier — le bloc qui porte ce bouton ne
         * s'affiche que pour un client réel, la garde ci-dessous ferme le dernier cas.
         */
        openClientEdition() {
            if (!this.selectedClient || this.isPassageClient) {
                return
            }

            this.clientPrefillName = ''
            this.clientEditId = Number(this.selectedClient.id)
            this.showClientSelector = true
        },
        /**
         * « Créer « X » » depuis la recherche : le popup existant s'ouvre, son formulaire
         * pré-rempli du nom déjà tapé. La création elle-même reste entièrement la sienne.
         */
        openClientCreation(term) {
            this.clientPrefillName = term || ''
            this.clientEditId = null
            this.changingClient = false
            this.showClientSelector = true
        },
        handleClientSelected(client) {
            this.changingClient = false
            // Les suggestions rendent le téléphone en `phone`, les autres sources en
            // `phone_number`. Le `??` ne recouvre jamais une valeur déjà présente.
            const next = {
                ...client,
                phone_number: client.phone_number ?? client.phone ?? null,
            }
            // Sélection PRÉCÉDENTE mémorisée AVANT de l'écraser : si l'enregistrement échoue,
            // c'est elle qu'on rétablit. Sur un devis neuf c'est le client de passage, que la
            // base garde tant que le rattachement n'a pas abouti.
            const previousClient = this.selectedClient
            const previousClientId = this.form.client_id

            this.selectedClient = next
            this.form.client_id = client.id
            this.persistClient(next, previousClient, previousClientId)
        },
        /**
         * Enregistre le client DU DEVIS dès qu'il change, sans attendre « Enregistrer ».
         *
         * Même principe que l'échéance et les moyens de paiement. Tant que le devis n'existe
         * pas (aucun `order_id`), le choix reste local : c'est la première ligne qui fait
         * naître l'enregistrement, et `/add-item` porte déjà `client_id`. Rien à rejouer au
         * watcher `order_id` pour cette raison.
         *
         * En cas d'échec, la sélection précédente est RÉTABLIE : sans ce repli, l'écran
         * afficherait un client que la base et le PDF n'ont pas retenu — exactement ce que
         * `persistDueDate` évite déjà pour l'échéance.
         *
         * @param {object} target        client que CET appel tente d'enregistrer
         * @param {?object} previousClient   sélection à rétablir en cas d'échec
         * @param {?number} previousClientId identifiant correspondant
         */
        async persistClient(target, previousClient, previousClientId) {
            if (!this.order_id || !target) return

            try {
                await axios.post('/back-office/v3/devis/quote-client', {
                    order_id: this.order_id,
                    client_id: target.id,
                })
            } catch (error) {
                // On ne rétablit QUE si la sélection courante est toujours celle que cet appel
                // enregistrait : une sélection plus récente ne doit pas être écrasée par
                // l'échec d'une plus ancienne (deux choix rapprochés).
                if (this.selectedClient === target) {
                    this.selectedClient = previousClient
                    this.form.client_id = previousClientId
                }

                this.$toast.warning(error.response?.data?.message ?? this.$t('devis.v3.error_quote_client'), {
                    timeout: 6000,
                })
            }
        },
        // Pré-sélection du "client de passage" par défaut sur un nouveau devis,
        // pour s'aligner sur le comportement de la caisse nomade.
        async initPassageClient() {
            try {
                const response = await axios.post('/back-office/v3/devis/store-passage-client')
                this.selectedClient = response.data
                this.form.client_id = response.data.id
            } catch (error) {
                console.error('Error initializing passage client:', error)
            }
        },
        handleCreateClient() {
            this.$toast.info(this.$t('devis.v3.client_creation_soon'))
        },
        togglePostIt() {
            // Bascule l'affichage du post-it qui contient la demande du client.
            if (this.postItVisible) {
                this.postItVisible = false
            } else if (this.$refs.postIt) {
                this.$refs.postIt.show()
            }
        },
        openItemStepper() {
            if (this.quoteLocked) return
            this.showItemStepper = true
        },
        /**
         * « Ajouter » — ouvre un BROUILLON LOCAL de ligne personnalisée. Aucun appel serveur :
         * on pose simplement l'objet `customDraft` (le template rend alors la ligne brouillon) et
         * on donne le focus à la désignation. La ligne n'est écrite en base qu'au commit
         * (cf. commitDraft). Garde client identique à l'ajout catalogue : jamais de ligne sans
         * client, et jamais deux brouillons à la fois.
         */
        startCustomDraft() {
            if (this.quoteLocked) return
            if (!this.selectedClient || this.customDraft || this.addingCustomLine || this.editingItemId !== null) {
                return
            }

            this.customDraft = {
                custom_label: '',
                quantity: 1,
                price_ht: 0,
                vat_rate: 0,
                is_optional: false,
            }
            this.draftSuggestions = { experiences: [], products: [], foods: [] }
            this.draftSuggestOpen = false

            this.$nextTick(() => {
                const input = this.$refs.customDraftDesignation
                if (input) {
                    ;(Array.isArray(input) ? input[0] : input).focus()
                }
            })
        },
        /**
         * Saisie de la désignation du brouillon : autocomplete catalogue DÉBOUNCÉ, déclenché dès 3
         * caractères. Sous 3 car. → on ferme et on vide. Option C : les suggestions ne font que
         * remplir le libellé (aucun ajout d'article).
         */
        onDraftDesignationInput() {
            const term = (this.customDraft?.custom_label || '').trim()
            if (this.draftSearchTimer) clearTimeout(this.draftSearchTimer)

            if (term.length < 3) {
                this.draftSuggestOpen = false
                this.draftSuggestions = { experiences: [], products: [], foods: [] }
                return
            }

            this.draftSearchTimer = setTimeout(async () => {
                // La ligne a pu être validée/annulée entre-temps.
                if (!this.customDraft) return
                try {
                    const { data } = await axios.get('/back-office/v3/devis/items/search', { params: { q: term } })
                    this.draftSuggestions = {
                        experiences: data?.experiences || [],
                        products: data?.products || [],
                        foods: data?.foods || [],
                    }
                    this.draftSuggestOpen = this.draftHasSuggestions
                } catch (e) {
                    // Suggestion best-effort : un échec n'empêche pas de saisir une ligne perso.
                    this.draftSuggestOpen = false
                }
            }, 300)
        },
        /**
         * Clic sur une suggestion : on remplit le libellé (option C — la ligne reste personnalisée)
         * et on ferme la liste. Le focus reste sur l'input (mousedown.prevent côté template).
         */
        pickDraftSuggestion(item, type) {
            if (!this.customDraft) return
            // Remplit la désignation + pré-remplit le tarif de l'article (PU HT + TVA). On MÉMORISE
            // l'article choisi (`_picked`) : s'il reste INCHANGÉ au commit, on ajoutera le VRAI article
            // du catalogue (pas de ligne perso / produit technique dupliqué). S'il est modifié, ou si
            // rien n'a été sélectionné, c'est une ligne personnalisée classique.
            this.customDraft.custom_label = item.name
            if (item.price_ht != null) this.customDraft.price_ht = Number(item.price_ht) || 0
            if (item.vat_rate != null) this.customDraft.vat_rate = Number(item.vat_rate) || 0
            this.customDraft._picked = {
                type, // 'products' | 'experiences' | 'foods'
                id: item.id,
                name: item.name,
                price_ht: Number(item.price_ht) || 0,
                vat_rate: Number(item.vat_rate) || 0,
                uuid: item.uuid || null, // foods uniquement (clé métier pour /add-food-items)
            }
            this.draftSuggestOpen = false
        },
        /**
         * La ligne brouillon correspond-elle EXACTEMENT à l'article sélectionné (désignation + PU HT
         * + TVA inchangés) ? La quantité n'entre pas en compte (ajouter N exemplaires d'un article
         * reste « l'article choisi »). Sert à décider : vrai article vs ligne personnalisée.
         */
        draftMatchesPick(draft, picked) {
            if (!picked) return false
            return (
                (draft.custom_label || '').trim() === (picked.name || '').trim() &&
                (Number(draft.price_ht) || 0) === (Number(picked.price_ht) || 0) &&
                (Number(draft.vat_rate) || 0) === (Number(picked.vat_rate) || 0)
            )
        },
        /**
         * Blur d'un input du brouillon. On diffère d'un tick (setTimeout 0) le temps que le focus
         * se pose sur sa nouvelle cible, puis on regarde si celle-ci est TOUJOURS dans la ligne
         * brouillon : si oui (navigation d'un champ à l'autre, clic sur ✓/✗) → on ne fait rien ;
         * si le focus est sorti de la ligne → on commit.
         */
        onDraftBlur() {
            setTimeout(() => {
                if (!this.customDraft) return
                const row = this.$refs.customDraftRow
                if (row && row.contains(document.activeElement)) return
                this.commitDraft()
            }, 0)
        },
        /**
         * Commit du brouillon → création en base. Désignation vide = on JETTE sans aucun appel.
         * Sinon : /add-custom-line (crée la ligne) puis /update-item (pose qté / PU HT / TVA /
         * option / libellé). Le serveur renvoie l'ordre recalculé : on réécrit l'état tel quel,
         * ZÉRO recalcul de total côté client (R3). En cas d'erreur on GARDE le brouillon pour un
         * nouvel essai — et on mémorise l'id déjà créé (`_orderItemId`) pour ne pas recréer une
         * seconde ligne au retry.
         */
        async commitDraft() {
            if (this.creatingDraft || !this.customDraft) return

            const draft = this.customDraft
            const label = (draft.custom_label || '').trim()
            if (!label) {
                // Brouillon vide : rien n'a été créé, on jette.
                this.customDraft = null
                return
            }

            this.creatingDraft = true
            try {
                // PRODUIT du catalogue sélectionné et NON modifié → on ajoute le VRAI produit (chemin
                // standard /add-product-items, le prix lu depuis la fiche produit), sans créer de ligne
                // perso ni de produit technique. (Expérience / Food : à venir — ils retombent pour
                // l'instant sur la ligne personnalisée ci-dessous.)
                if (draft._picked && draft._picked.type === 'products' && this.draftMatchesPick(draft, draft._picked)) {
                    const oldOrderIdP = this.order_id
                    const addReal = await axios.post('/back-office/v3/devis/add-product-items', {
                        client_id: this.selectedClient.id,
                        order_id: this.order_id,
                        due_date: this.form.due_date,
                        template_id: this.form.template_id,
                        note: this.note,
                        client_request: this.clientRequest,
                        products: [{ id: draft._picked.id, quantity: Number(draft.quantity) || 1 }],
                        is_optional: !!draft.is_optional,
                    })
                    if (addReal.data.success) {
                        this.order_id = addReal.data.order_id
                        this.items = addReal.data.order.items
                        this.updateTotalsFromOrder(addReal.data.order)
                        if (this.$refs.devisPopup) {
                            this.$refs.devisPopup.version++
                            this.$refs.devisPopup.buildDocumentURL()
                        }
                        if (!oldOrderIdP && this.order_id) {
                            window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                        }
                        this.customDraft = null
                    } else {
                        this.$toast.error(this.$t('devis.v3.error_adding'))
                    }
                    return
                }

                // EXPÉRIENCE du catalogue sélectionnée et NON modifiée → on ajoute la VRAIE prestation
                // (chemin /add-experience-undated), comme une ligne NON DATÉE : ni espace ni créneau
                // choisis ici, AUCUN stepper ne s'ouvre. La date, l'espace et les participants
                // définitifs seront finalisés à la popup de validation existante (expérience non datée).
                // Les participants = la quantité de la ligne. (Food : à venir — retombe sur la ligne
                // personnalisée ci-dessous.)
                if (draft._picked && draft._picked.type === 'experiences' && this.draftMatchesPick(draft, draft._picked)) {
                    const oldOrderIdE = this.order_id
                    const addExp = await axios.post('/back-office/v3/devis/add-experience-undated', {
                        client_id: this.selectedClient.id,
                        order_id: this.order_id,
                        due_date: this.form.due_date,
                        template_id: this.form.template_id,
                        note: this.note,
                        client_request: this.clientRequest,
                        prestation_id: draft._picked.id,
                        quantity: Number(draft.quantity) || 1,
                        is_optional: !!draft.is_optional,
                    })
                    if (addExp.data.success) {
                        this.order_id = addExp.data.order_id
                        this.items = addExp.data.order.items
                        this.updateTotalsFromOrder(addExp.data.order)
                        if (this.$refs.devisPopup) {
                            this.$refs.devisPopup.version++
                            this.$refs.devisPopup.buildDocumentURL()
                        }
                        if (!oldOrderIdE && this.order_id) {
                            window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                        }
                        this.customDraft = null
                    } else {
                        this.$toast.error(this.$t('devis.v3.error_adding'))
                    }
                    return
                }

                // FOOD du catalogue sélectionné et NON modifié → on ajoute le VRAI food (chemin
                // standard /add-food-items, prix lu depuis la fiche). Canal (food_type) laissé au
                // DÉFAUT côté serveur — ajustable ensuite. Pas de ligne perso / produit technique.
                if (
                    draft._picked &&
                    draft._picked.type === 'foods' &&
                    draft._picked.uuid &&
                    this.draftMatchesPick(draft, draft._picked)
                ) {
                    const oldOrderIdF = this.order_id
                    const addFood = await axios.post('/back-office/v3/devis/add-food-items', {
                        client_id: this.selectedClient.id,
                        order_id: this.order_id,
                        due_date: this.form.due_date,
                        template_id: this.form.template_id,
                        note: this.note,
                        client_request: this.clientRequest,
                        foods: [{ uuid: draft._picked.uuid, quantity: Number(draft.quantity) || 1 }],
                        is_optional: !!draft.is_optional,
                    })
                    if (addFood.data.success) {
                        this.order_id = addFood.data.order_id
                        this.items = addFood.data.order.items
                        this.updateTotalsFromOrder(addFood.data.order)
                        if (this.$refs.devisPopup) {
                            this.$refs.devisPopup.version++
                            this.$refs.devisPopup.buildDocumentURL()
                        }
                        if (!oldOrderIdF && this.order_id) {
                            window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                        }
                        this.customDraft = null
                    } else {
                        this.$toast.error(this.$t('devis.v3.error_adding'))
                    }
                    return
                }

                const oldOrderId = this.order_id
                let orderItemId = draft._orderItemId || null

                // Première tentative : on crée la ligne. Un retry après échec de l'update réutilise
                // l'id déjà obtenu et saute cette étape (pas de ligne en double).
                if (!orderItemId) {
                    const addResponse = await axios.post('/back-office/v3/devis/add-custom-line', {
                        client_id: this.selectedClient.id,
                        order_id: this.order_id,
                        due_date: this.form.due_date,
                        template_id: this.form.template_id,
                        note: this.note,
                        client_request: this.clientRequest,
                        designation: label,
                    })

                    if (!addResponse.data.success) {
                        this.$toast.error(this.$t('devis.v3.error_adding'))
                        return
                    }

                    this.order_id = addResponse.data.order_id
                    orderItemId = addResponse.data.order_item_id
                    draft._orderItemId = orderItemId

                    if (!oldOrderId && this.order_id) {
                        window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                    }
                }

                const priceHt = Number(draft.price_ht) || 0
                const vatRate = Number(draft.vat_rate) || 0
                const quantity = Number(draft.quantity) || 1

                const response = await axios.post('/back-office/v3/devis/update-item', {
                    order_id: this.order_id,
                    order_item_id: orderItemId,
                    quantity,
                    price_ht: priceHt,
                    // Prix TTC unitaire : seule valeur dérivée ici, exigée par le contrat
                    // update-item. Les totaux du devis restent 100 % serveur.
                    price: priceHt * (1 + vatRate / 100),
                    vat_rate: vatRate,
                    is_optional: !!draft.is_optional,
                    custom_label: label,
                })

                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)

                    if (this.$refs.devisPopup) {
                        this.$refs.devisPopup.version++
                        this.$refs.devisPopup.buildDocumentURL()
                    }

                    this.customDraft = null
                }
            } catch (error) {
                // On garde le brouillon pour que l'utilisateur puisse réessayer.
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))
            } finally {
                this.creatingDraft = false
            }
        },
        /**
         * Bouton ✗ : abandonne le brouillon LOCAL sans aucun appel serveur.
         */
        cancelDraft() {
            if (this.creatingDraft) return
            this.customDraft = null
        },
        /**
         * [LEGACY — plus déclenché par le bouton « + ajouter »] Ancien flux « créer-puis-affiner » :
         * créait la ligne côté serveur dès le clic puis l'ouvrait en mode capture. Remplacé par le
         * brouillon local (startCustomDraft / commitDraft). Conservé car `customLineCaptureId` /
         * `autoSaveCapture` / `cancelCapture` restent référencés par l'édition de ligne existante.
         *
         * Garde client identique à l'ajout catalogue : jamais de ligne sans client.
         */
        async openCustomLine() {
            if (!this.selectedClient || this.addingCustomLine || this.editingItemId !== null) {
                return
            }

            this.addingCustomLine = true
            try {
                const oldOrderId = this.order_id
                const response = await axios.post('/back-office/v3/devis/add-custom-line', {
                    client_id: this.selectedClient.id,
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                    template_id: this.form.template_id,
                    note: this.note,
                    client_request: this.clientRequest,
                    // Désignation de départ, remplacée par la saisie inline juste après.
                    designation: this.$t('devis.v3.custom_line_default'),
                })

                if (!response.data.success) {
                    return
                }

                this.order_id = response.data.order_id
                this.items = response.data.order.items
                this.updateTotalsFromOrder(response.data.order)

                if (!oldOrderId && this.order_id) {
                    window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                }

                // Ouvre la ligne créée en mode « capture » : seul le champ désignation est
                // affiché, validation auto sur Entrée / blur / Escape.
                const newLine = (this.items || []).find((it) => it.id === response.data.order_item_id)
                if (newLine) {
                    this.justCreatedCustomLineId = newLine.id
                    this.editingItemId = newLine.id
                    this.customLineCaptureId = newLine.id
                    this.editForm = {
                        custom_label: newLine.custom_label || '',
                        quantity: newLine.quantity ?? 1,
                        price_ht: newLine.price_ht ?? 0,
                        vat_rate: newLine.vat_rate ?? 0,
                        can_edit_amounts: true,
                        is_optional: !!newLine.is_optional,
                    }
                }
            } catch (error) {
                console.error('Error adding custom line:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))
            } finally {
                this.addingCustomLine = false
            }
        },
        /**
         * Ajoute une OPTION personnalisée (ligne enfant) sous une ligne PRODUIT.
         *
         * Réutilise l'endpoint /add-custom-line avec `parent_order_item_id` : le serveur crée le
         * produit technique « depuis devis » et la ligne enfant à 0 €, puis recalcule les totaux
         * et régénère le document — exactement comme une ligne personnalisée, mais rattachée. La
         * nouvelle option s'ouvre aussitôt en édition de sa désignation (le prix s'ajuste ensuite
         * via le crayon de sa cellule).
         */
        async addOption(parentLine) {
            if (this.quoteLocked) return
            if (
                !this.selectedClient ||
                !parentLine ||
                this.addingCustomLine ||
                this.editingItemId !== null ||
                this.cellEdit.saving
            ) {
                return
            }

            this.addingCustomLine = true
            try {
                const response = await axios.post('/back-office/v3/devis/add-custom-line', {
                    client_id: this.selectedClient.id,
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                    template_id: this.form.template_id,
                    note: this.note,
                    client_request: this.clientRequest,
                    parent_order_item_id: parentLine.id,
                    // Désignation de départ, remplacée par la saisie inline juste après.
                    designation: this.$t('devis.v3.custom_line_default'),
                })

                if (!response.data.success) {
                    return
                }

                this.items = response.data.order.items
                this.updateTotalsFromOrder(response.data.order)
                if (this.$refs.devisPopup) {
                    this.$refs.devisPopup.version++
                    this.$refs.devisPopup.buildDocumentURL()
                }

                // Ouvre l'option créée en édition de sa désignation (crayon de cellule).
                const newOption = (this.items || []).find((it) => it.id === response.data.order_item_id)
                if (newOption) {
                    this.$nextTick(() => this.startCellEdit(newOption, 'custom_label'))
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))
            } finally {
                this.addingCustomLine = false
            }
        },
        async handleAddItem(reservation) {
            if (this.quoteLocked) return
            if (!this.selectedClient) {
                this.$toast.error(this.$t('devis.v3.select_client_first_long'))
                this.showItemStepper = false
                this.showClientSelector = true
                return
            }
            try {
                const payload = {
                    client_id: this.selectedClient.id,
                    reservation: reservation,
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                    template_id: this.form.template_id,
                    note: this.note,
                    client_request: this.clientRequest,
                }
                const response = await axios.post('/back-office/v3/devis/add-item', payload)
                if (response.data.success) {
                    const oldOrderId = this.order_id
                    this.order_id = response.data.order_id
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    this.$toast.success(this.$t('devis.v3.item_added'))

                    // Si c'est un nouvel ordre, on met à jour l'URL sans recharger la page
                    if (!oldOrderId && this.order_id) {
                        window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                    }

                    return true
                }

                return false
            } catch (error) {
                console.error('Error adding item:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))

                return false
            }
        },
        /**
         * Enregistrement d'un LOT composé dans la popup : plusieurs prestations, des plats et
         * des produits, en une seule validation.
         *
         * Les appels sont SÉQUENTIELS et non parallèles : le premier crée le devis et renvoie
         * son `order_id`, que les suivants doivent réutiliser. Les lancer ensemble créerait
         * autant de devis que d'appels.
         *
         * Chaque type garde son propre chemin d'enregistrement — on ne fait qu'enchaîner les
         * gestionnaires existants. Ajouter trois lignes d'un coup revient exactement à les
         * ajouter une par une, dans cet ordre.
         *
         * Au premier échec on s'arrête : la popup reste ouverte avec ce qui n'est pas passé,
         * plutôt que de poursuivre et laisser l'utilisateur deviner ce qui a été enregistré.
         */
        /**
         * Enregistrement d'un LOT composé dans la popup : prestations, plats et produits, en
         * une seule requête et une seule transaction côté serveur.
         *
         * Les lignes écrites sont exactement celles qu'auraient produites des ajouts séparés —
         * chaque type garde son service d'écriture. Ce qui change, c'est que dix lignes
         * s'ajoutent ou aucune, et qu'un seul accusé de réception est affiché.
         */
        async handleAddBatch({ experiences, foods, products, resolve, reject }) {
            if (this.quoteLocked) {
                if (typeof reject === 'function') reject()
                return
            }
            if (!this.selectedClient) {
                this.$toast.error(this.$t('devis.v3.select_client_first_long'))
                this.showItemStepper = false
                this.showClientSelector = true
                reject()
                return
            }

            try {
                const response = await axios.post('/back-office/v3/devis/add-lines', {
                    client_id: this.selectedClient.id,
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                    template_id: this.form.template_id,
                    note: this.note,
                    client_request: this.clientRequest,
                    reservations: experiences || [],
                    foods: foods?.foods || [],
                    packs: foods?.packs || [],
                    products: products?.products || [],
                })

                if (!response.data.success) {
                    reject()
                    return
                }

                const oldOrderId = this.order_id
                this.order_id = response.data.order_id
                this.items = response.data.order.items
                this.updateTotalsFromOrder(response.data.order)
                this.$toast.success(this.$t('devis.v3.item_added'))

                if (!oldOrderId && this.order_id) {
                    window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                }

                resolve()
            } catch (error) {
                console.error('Error adding lines:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))
                reject()
            }
        },
        /**
         * Enregistrement des lignes RESTO composées dans la popup.
         *
         * Même contrat que `handleAddItem` (client obligatoire, création du devis au
         * premier article, mise à jour de l'URL), mais sur son propre endpoint : un plat
         * n'a ni créneau ni participants. Le devis peut déjà contenir des prestations ou
         * d'autres plats — on ne fait qu'ajouter.
         *
         * `resolve` / `reject` referment la popup uniquement si l'enregistrement a
         * réussi : sur erreur, le panier composé reste à l'écran.
         */
        async handleAddFoodItems({ payload, resolve, reject }) {
            if (this.quoteLocked) {
                if (typeof reject === 'function') reject()
                return
            }
            if (!this.selectedClient) {
                this.$toast.error(this.$t('devis.v3.select_client_first_long'))
                this.showItemStepper = false
                this.showClientSelector = true
                reject()
                return
            }

            try {
                const response = await axios.post('/back-office/v3/devis/add-food-items', {
                    client_id: this.selectedClient.id,
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                    template_id: this.form.template_id,
                    note: this.note,
                    client_request: this.clientRequest,
                    foods: payload.foods,
                    packs: payload.packs,
                })

                if (!response.data.success) {
                    reject()
                    return
                }

                const oldOrderId = this.order_id
                this.order_id = response.data.order_id
                this.items = response.data.order.items
                this.updateTotalsFromOrder(response.data.order)
                this.$toast.success(this.$t('devis.v3.item_added'))

                if (!oldOrderId && this.order_id) {
                    window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                }

                resolve()
            } catch (error) {
                console.error('Error adding food items:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))
                reject()
            }
        },
        /**
         * Enregistrement des lignes PRODUIT sélectionnées dans la popup.
         *
         * Contrat identique au resto (client obligatoire, création du devis au premier
         * article, mise à jour de l'URL) sur son propre endpoint : la ligne produit est
         * plate, calquée sur la caisse nomade.
         */
        async handleAddProductItems({ payload, resolve, reject }) {
            if (this.quoteLocked) {
                if (typeof reject === 'function') reject()
                return
            }
            if (!this.selectedClient) {
                this.$toast.error(this.$t('devis.v3.select_client_first_long'))
                this.showItemStepper = false
                this.showClientSelector = true
                reject()
                return
            }

            try {
                const response = await axios.post('/back-office/v3/devis/add-product-items', {
                    client_id: this.selectedClient.id,
                    order_id: this.order_id,
                    due_date: this.form.due_date,
                    template_id: this.form.template_id,
                    note: this.note,
                    client_request: this.clientRequest,
                    products: payload.products,
                })

                if (!response.data.success) {
                    reject()
                    return
                }

                const oldOrderId = this.order_id
                this.order_id = response.data.order_id
                this.items = response.data.order.items
                this.updateTotalsFromOrder(response.data.order)
                this.$toast.success(this.$t('devis.v3.item_added'))

                if (!oldOrderId && this.order_id) {
                    window.history.pushState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
                }

                resolve()
            } catch (error) {
                console.error('Error adding product items:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_adding'))
                reject()
            }
        },
        // `group` n'est fourni que pour une ligne INCLUSE (billet d'un conteneur simpleTicket,
        // option / supplément / produit de menu d'une ligne resto).
        async handleRemoveItem(orderItemId, group = null) {
            if (this.quoteLocked) return
            // Retirer le dernier billet d'un CONTENEUR emporte la ligne principale entière : cette
            // suppression est donc soumise au même garde-fou « dernier article » qu'une ligne
            // principale. Tant qu'il reste au moins un autre billet, le serveur ne retire que la
            // part de participants du billet et la ligne principale subsiste : aucun garde-fou.
            //
            // Une ligne resto n'est PAS un conteneur : retirer son dernier supplément laisse le
            // plat en place, le garde-fou ne s'applique donc jamais à ses enfants.
            const removesWholeLine = !group || (group.parent.is_container && group.children.length <= 1)
            if (removesWholeLine && this.itemGroups.length <= 1) {
                this.$toast.error(this.$t('devis.v3.cannot_remove_last'))
                return
            }
            // Confirmation via la modale partagée du projet (ConfirmationDialog) plutôt que le
            // confirm() natif du navigateur, qui ignore la charte et bloque le thread.
            this.pendingRemoval = {
                orderItemId,
                message:
                    group && removesWholeLine
                        ? this.$t('devis.v3.confirm_remove_last_child')
                        : this.$t('devis.v3.confirm_remove'),
            }
        },
        startItemNote(item) {
            if (this.quoteLocked || this.savingNote) return
            this.noteEditingId = item.id
            this.noteDraft = item.comment || ''
        },
        cancelItemNote() {
            this.noteEditingId = null
            this.noteDraft = ''
        },
        handleItemNoteBlur(event, item) {
            if (this.savingNote) return
            const comment = String(event?.target?.value ?? this.noteDraft).trim()
            // Rien à enregistrer : champ vidé, ou texte IDENTIQUE à celui déjà en base
            // (ouverture puis Tab, simple refocus). On ferme sans POST ni toast — une
            // sauvegarde à blanc ferait régénérer tout le devis (SyncTvaOrder +
            // updateOrderPricesAndTva + makeQuote) pour un contenu inchangé.
            if (!comment || comment === String(item.comment || '').trim()) {
                this.cancelItemNote()
                return
            }
            this.noteDraft = comment
            this.saveItemNote(item, comment)
        },
        handleItemNoteClickOutside(event) {
            if (this.noteEditingId === null || this.savingNote) return

            const editor = this.$el.querySelector('.item-note-editor')
            if (!editor || editor.contains(event.target)) return

            const item = (this.items || []).find((line) => String(line.id) === String(this.noteEditingId))
            const textarea = editor.querySelector('textarea')
            if (item && textarea) this.handleItemNoteBlur({ target: textarea }, item)
        },
        handleItemNoteKeydown(event, item) {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                this.handleItemNoteBlur(event, item)
            } else if (event.key === 'Escape') {
                event.preventDefault()
                this.cancelItemNote()
            }
        },
        async saveItemNote(item, value = this.noteDraft) {
            const comment = String(value || '').trim()
            if (this.savingNote || !comment) return
            this.savingNote = true
            try {
                const response = await axios.post('/back-office/v3/devis/update-item', {
                    order_id: this.order_id,
                    order_item_id: item.id,
                    comment,
                })
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    if (this.noteEditingId === item.id) this.cancelItemNote()
                    this.$toast.success(this.$t('devis.v3.note_saved'))
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('devis.v3.note_error'))
            } finally {
                this.savingNote = false
            }
        },
        cancelRemoval() {
            this.pendingRemoval = null
        },
        async confirmRemoval() {
            if (!this.pendingRemoval) return

            const orderItemId = this.pendingRemoval.orderItemId
            this.pendingRemoval = null

            try {
                const response = await axios.post('/back-office/v3/devis/remove-item', {
                    order_id: this.order_id,
                    order_item_id: orderItemId,
                })
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    this.$toast.success(this.$t('devis.v3.item_removed'))
                }
            } catch (error) {
                console.error('Error removing item:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_removing'))
            }
        },
        async handleDuplicateItem(orderItemId) {
            if (this.quoteLocked) return
            if (this.duplicatingItemId !== null) return
            this.duplicatingItemId = orderItemId
            try {
                const response = await axios.post('/back-office/v3/devis/duplicate-item', {
                    order_id: this.order_id,
                    order_item_id: orderItemId,
                })
                if (response.data.success) {
                    this.items = response.data.order.items
                    this.updateTotalsFromOrder(response.data.order)
                    this.$toast.success(this.$t('devis.v3.item_duplicated'))
                }
            } catch (error) {
                console.error('Error duplicating item:', error)
                this.$toast.error(error.response?.data?.message ?? this.$t('devis.v3.error_duplicating'))
            } finally {
                this.duplicatingItemId = null
            }
        },
        updateTotalsFromOrder(order) {
            // `total_price` est le total BRUT des lignes (prix × quantité), `remise_price` le
            // cumul des remises et `tva` la TVA déjà nette de remise
            // (OrderService::updateOrderPricesAndTva). On affiche donc des montants NETS, pour que
            // HT + TVA = TTC. Sans remise, remise vaut 0 et les valeurs sont inchangées.
            const grossTtc = parseFloat(order.total_price) || 0
            const discount = parseFloat(order.remise_price) || 0
            const tva = parseFloat(order.tva) || 0
            const ttc = grossTtc - discount
            const ht = ttc - tva

            this.totals.ht = ht
            this.totals.net_ht = ht
            this.totals.tax = tva
            this.totals.ttc = ttc
            this.totals.discount = discount

            // Le serveur re-vérifie le code promo du devis après chaque modification de ligne. S'il
            // n'est plus applicable (montant du panier hors des bornes du code, quota épuisé…), il l'a
            // RETIRÉ du devis et renvoie `promo_warning` : on retire la puce côté client et on prévient
            // l'utilisateur, libre à lui d'en rechoisir un. La décision reste 100 % serveur.
            if (order.promo_warning) {
                this.discountCode = null
                const toast = this.$toast
                if (toast) (toast.warning || toast.info || toast.error || toast.success)?.call(toast, order.promo_warning)
            }
        },
        formatCurrency(value) {
            // Garde-fou : un montant absent ou non numérique s'affiche « 0,00 € », jamais
            // « NaN € » — un montant illisible est pire qu'un montant nul sur un devis.
            const amount = Number(value)
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
                Number.isFinite(amount) ? amount : 0
            )
        },
        togglePriceDisplayMenu() {
            this.priceDisplayMenuOpen = !this.priceDisplayMenuOpen
        },
        closePriceDisplayMenu() {
            this.priceDisplayMenuOpen = false
        },
        selectPriceDisplayMode(mode) {
            this.priceDisplayMode = mode
            this.closePriceDisplayMenu()
            this.$nextTick(() => this.$refs.priceDisplayTrigger?.focus())
        },
        // Remise niveau devis (bloc totaux) — passage état 1 → état 2.
        openDiscountEditor() {
            if (this.quoteLocked) return
            this.discountEditing = true
        },
        openDepositEditor() {
            if (this.quoteLocked) return
            this.depositEditing = true
            this.$nextTick(() => this.$refs.depositInput?.focus())
        },
        cancelDeposit() {
            if (this.quoteLocked) return
            this.depositEditing = false
            this.depositValue = null
            this.depositMode = 'percentage'
        },
        // × : annule la remise et revient à l'état 1 (lien). Le code choisi est aussi
        // retiré du devis (persistance).
        cancelDiscount() {
            if (this.quoteLocked) return
            this.discountEditing = false
            this.discountCodeListOpen = false
            this.discountCode = null
            this.manualDiscountValue = null
            this.savePromoCode()
        },
        toggleDiscountCodeList() {
            if (this.quoteLocked) return
            this.discountCodeListOpen = !this.discountCodeListOpen
        },
        closeDiscountCodeList() {
            this.discountCodeListOpen = false
        },
        // Choix d'un code promo : on l'affiche et on l'ENREGISTRE sur le devis. AUCUNE remise
        // n'est calculée ici — elle se matérialise à la conversion, en fail-open. `null`
        // efface le code enregistré.
        selectPromoCode(code) {
            if (this.quoteLocked) return
            this.discountCode = code || null
            this.discountCodeListOpen = false
            this.savePromoCode()
        },
        // Valeur NOMINALE d'un code, telle qu'elle est proposée : « 10 % » ou « 12,00 € ».
        // C'est l'intention affichée, jamais une déduction — le devis ne retire rien de
        // ses totaux.
        formatPromoValue(promo) {
            const amount = Number(promo?.discount_amount)
            if (!Number.isFinite(amount)) return ''
            return String(promo?.discount_type).toLowerCase() === 'percentage' ? `${amount} %` : this.formatCurrency(amount)
        },
        // Enregistre le code CHOISI sur le devis (devis_invoices.promo_code). Rien n'est
        // appliqué : aucune remise n'est calculée ni figée ici. Nécessite un devis déjà
        // créé (order_id).
        async savePromoCode() {
            if (this.quoteLocked) return
            if (!this.order_id) return
            try {
                await axios.post('/back-office/v3/devis/promo-code', {
                    order_id: this.order_id,
                    promo_code: this.discountCode || null,
                })
            } catch (error) {
                // Code refusé (invalide pour l'échéance, désactivé, inexistant) : on affiche
                // le motif et on revient à « aucun code » — on ne laisse pas un code non
                // enregistré sélectionné à l'écran.
                this.$toast.error(error.response?.data?.message || "Erreur lors de l'enregistrement du code promo")
                this.discountCode = null
            }
        },
        formatDate(date) {
            if (!date) return ''
            // Handle YYYY-MM-DD string directly to avoid timezone shifts
            if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
                const [year, month, day] = date.substring(0, 10).split('-')
                return `${day}/${month}/${year}`
            }
            return new Date(date).toLocaleDateString('fr-FR')
        },
    },
    mounted() {
        this.getTemplates()
        // Codes promo proposables dans le sélecteur du bloc totaux (injectés par le serveur).
        this.promoCodes = this.initialOrder?.promo_codes || []
        // Code promo déjà enregistré sur le devis : on le réaffiche dans la ligne « code
        // appliqué » SANS rouvrir l'éditeur (état 1) et SANS ré-enregistrer — c'est la lecture
        // d'un état existant, pas une nouvelle application.
        if (this.initialOrder?.selected_promo_code) {
            this.discountCode = this.initialOrder.selected_promo_code
        }
        if (this.initialOrder?.id) {
            this.order_id = this.initialOrder.id
            this.getAppliedQuote()
            this.items = this.initialOrder.items || []
            this.note = this.initialOrder.note || ''
            this.clientRequest = this.initialOrder.client_request || ''

            if (this.initialOrder.quote_logo) {
                this.form.logo = this.initialOrder.quote_logo
                this.initialLogo = this.initialOrder.quote_logo
            }

            if (this.initialOrder.user) {
                this.selectedClient = this.initialOrder.user
                console.log('Initial client debug:', this.selectedClient)
                this.form.client_id = this.initialOrder.user.id
            }

            // Ancre de calcul : la date de création du devis, pas aujourd'hui.
            if (this.initialOrder.created_at) {
                this.issueDate = String(this.initialOrder.created_at).substring(0, 10)
            }

            if (this.initialOrder.due_date) {
                this.form.due_date = this.initialOrder.due_date.substring(0, 10)
                // Le sélecteur de durée doit refléter l'échéance ENREGISTRÉE : sinon il
                // afficherait « 1 mois » sur un devis échéancé autrement, et le premier
                // changement de durée déplacerait la date sans que rien ne l'annonce.
                // Écriture silencieuse (skipValidityWatch) : on aligne l'affichage, on ne
                // rejoue ni le calcul ni le contrôle sur une donnée déjà enregistrée.
                const validity = daysBetween(this.issueDate, this.form.due_date)
                if (validity > 0 && validity !== this.form.validity) {
                    this.skipValidityWatch = true
                    this.form.validity = validity
                }
            }

            // Délai de relance enregistré sur le devis. Écriture silencieuse : on aligne
            // l'affichage sur la base, on ne réenregistre pas une valeur qui en vient.
            // Absent (null) = jamais choisi : on laisse le défaut de l'écran, que le
            // watcher `order_id` a déjà transmis à la création du devis.
            if (this.initialOrder.auto_relaunch_days) {
                const storedDays = Number(this.initialOrder.auto_relaunch_days)
                if (storedDays !== this.form.auto_reminder) {
                    this.skipAutoReminderWatch = true
                    this.form.auto_reminder = storedDays
                }
            }

            this.updateTotalsFromOrder(this.initialOrder)

            // Si on a déjà un order_id mais que l'URL n'est pas à jour
            const currentPath = window.location.pathname
            if (!currentPath.includes(`/create/${this.order_id}`)) {
                window.history.replaceState({}, '', `/back-office/v3/devis/create/${this.order_id}`)
            }
        }
        // Pas de logo spécifique au devis : on utilise celui de l'établissement.
        if (!this.form.logo && this.initialOrder?.establishment_logo) {
            this.form.logo = this.initialOrder.establishment_logo
            this.initialLogo = this.initialOrder.establishment_logo
        }
        // Si l'établissement a un logo, le champ est en lecture seule
        this.logoFromEstablishment = !!this.initialOrder?.establishment_logo
        document.body.classList.add('full-page-popup')

        // Nouveau devis sans client pré-existant : on sélectionne le client de passage par défaut
        // (comme en caisse). Sur un devis existant ou déjà lié à un client, on ne touche à rien.
        if (!this.selectedClient) {
            this.initPassageClient()
        }
        this.$nextTick(() => this.initSortable())
        document.addEventListener('click', this.handleItemNoteClickOutside, true)
        document.addEventListener('click', this.handleClickOutsideSend)
    },
    beforeDestroy() {
        if (this.sortableInstance) {
            this.sortableInstance.destroy()
        }
        document.removeEventListener('click', this.handleItemNoteClickOutside, true)
        document.removeEventListener('click', this.handleClickOutsideSend)
        document.body.classList.remove('full-page-popup')
    },
}
</script>

<style scoped lang="scss">
/* Devis accepté (« Gagné ») : bandeau de rappel + état verrouillé de l'éditeur.
   Tout est neutralisé SAUF la colonne « Option » (col-optional), qui reste
   pleinement interactive — le serveur autorise encore ce seul changement. */
.quote-locked-banner {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 0 24px 4px;
    padding: 10px 16px;
    border-radius: 12px;
    background: #f4e9fb;
    color: #7d2dbd;
    border: 1px solid #dbc1f0;
    font-size: 13px;
}
.quote-locked-banner__body {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.quote-locked-banner__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.quote-accepted-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 999px;
    background: #16a34a;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.quote-signature {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.quote-signature__meta {
    color: #6b7280;
    font-size: 12px;
}
.quote-signature__img {
    max-height: 70px;
    max-width: 260px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 4px 8px;
    object-fit: contain;
}

/* Bouton d'envoi : le wrapper garde le survol (donc le tooltip natif `title`),
   seul son contenu devient inerte. */
.quote-locked-btn {
    cursor: not-allowed;

    > * {
        opacity: 0.5;
        pointer-events: none;
    }
}

/* Canevas verrouillé : les contrôles désactivés prennent l'aspect inerte.
   La colonne Option est explicitement ré-autorisée. */
.main-canvas.quote-locked {
    :disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    .col-optional,
    .col-optional * {
        cursor: pointer;
        opacity: 1;
        pointer-events: auto;
    }

    /* …mais une case Option VERROUILLÉE (ligne non optionnelle d'un devis accepté : on ne peut plus
       la COCHER, seulement décocher une option existante) reprend l'aspect « interdit » — curseur
       cercle barré + grisée — tout en gardant le hover pour afficher le tooltip de verrou. */
    .col-optional .option-checkbox:disabled {
        cursor: not-allowed;
        opacity: 0.5;
        pointer-events: auto;
    }
}

/* Header */
.header-v3 {
    position: sticky;
    top: 0;
    z-index: 100;

    h1 {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
        color: #1e293b;
    }
}

.btn-outline-subtle {
    display: flex;
    align-items: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}

.btn-action {
    display: flex;
    align-items: center;
    border-radius: 8px;
    cursor: pointer;
}

.btn-envoyer-more {
    border-radius: 0 8px 8px 0;
    padding: 8px 8px;
    display: flex;
    align-items: center;
    border-left: 1px solid #fff;
}

/* Layout */
.content-layout {
    margin: 20px auto;
    gap: 20px;
}

.main-canvas {
    display: flex;
    flex-direction: column;
    gap: 20px;
    background-color: #d2ddf429;
    box-shadow: 0px 4.56px 4.56px 4.56px #0000001a;
    border-radius: 12px;
    height: 75vh;
    overflow: auto;
    width: 100%;
}

.dark .main-canvas {
    box-shadow: 0px 4.56px 4.56px 4.56px #ffffff1a;
}

.top-cards {
    display: grid;
    grid-template-columns: 200px 1fr 350px;
    gap: 20px;
    align-items: start;
}

.logo-column {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
}

.card-logo {
    min-height: 120px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
}

@media (min-width: 1024px) {
    .card-logo {
        min-height: unset;
        max-width: 160px;
    }
}

.card-postit {
    background: #fff9be;
    font-family: 'Kalam Regular';
    padding: 24px;
    border-radius: 4px;
    box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.05);
    min-width: 350px;
    min-height: 315px;
    rotate: -2.5deg;
    position: relative;

    .postit-textarea {
        width: 100%;
        height: 100%;
        min-height: 250px;
        background: transparent;
        border: none;
        outline: none;
        resize: none;
        font-family: inherit;
        line-height: 1.5;
        color: #334155;

        &::placeholder {
            color: #94a3b8;
            font-style: italic;
        }
    }
}

.card-postit::after {
    content: '';
    position: absolute;
    bottom: -20px;
    background:
        radial-gradient(68.57% 68.57% at 50.16% 0%, rgba(0, 0, 0, 0.2) 11.98%, rgba(0, 0, 0, 0) 76.04%),
        radial-gradient(45.71% 45.71% at 31.39% -20%, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 100%)
            /* warning: gradient uses a rotation that is not supported by CSS and may not behave as expected */;
    width: 100%;
    height: 20px;
    left: 0;
}

.card-quote-info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: right;
}

.client-selector-box {
    position: relative;
    text-align: left;

    .client-summary__actions {
        opacity: 0;
        transition: opacity 0.12s ease;
    }
    .client-summary:hover .client-summary__actions {
        opacity: 1;
    }
    .client-summary__badge {
        margin-left: 7px;
        color: #7d2dbd;
        font-size: 10px;
        background-color: #c299ff0d;
    }

    /* Lien vers la fiche client : discret au repos, il ne doit pas concurrencer
       « changer de client », qui reste l'action principale de la carte. */
    /* Porté à la fois par un lien (fiche complète) et par un bouton (retouche rapide) :
       les remises à zéro ci-dessous font que les deux se lisent pareil, sans dépendre du
       style natif du bouton. */
    .client-record-link {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        margin-top: 6px;
        padding: 0;
        width: fit-content;
        background: none;
        border: none;
        color: #7d2dbd;
        font-size: 12.5px;
        text-decoration: none;
        opacity: 0.85;
        cursor: pointer;
        transition: opacity 0.18s ease;

        &:hover {
            opacity: 1;
            text-decoration: underline;
        }
    }

    .client-empty {
        display: flex;
        align-items: center;
        gap: 12px;

        .icon-user {
            width: 32px;
            height: 32px;
            background: #f1f5f9;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #94a3b8;
        }

        .text {
            flex: 1;

            strong {
                display: block;
                font-size: 13px;
                color: #334155;
            }

            span {
                font-size: 11px;
                color: #94a3b8;
            }
        }
    }
}

.table-responsive-wrapper {
    overflow-x: auto;
    width: 100%;

    &::-webkit-scrollbar {
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }
}

.dark .table-responsive-wrapper {
    &::-webkit-scrollbar-thumb {
        background: #4a4a6e;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #5a5a7e;
    }
}

.devis-table {
    --devis-table-columns: 40px minmax(280px, 1fr) 56px 96px 140px 96px 72px 148px 64px;

    display: block;
    width: 100%;
    min-width: 1050px;
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 7px;

    thead,
    tbody {
        display: block;
        width: 100%;
    }

    thead tr,
    tbody tr {
        display: grid;
        grid-template-columns: var(--devis-table-columns);
        column-gap: 8px;
        align-items: center;
        width: 100%;
    }

    thead tr {
        background: #7d2dbd;
        border-radius: 7px;
    }

    thead th {
        min-width: 0;
        box-sizing: border-box;
        padding: 12px 4px;
        font-family: 'Poppins SemiBold', sans-serif;
        color: white;
        font-size: 12px;
        text-align: center;

        &:first-child {
            border-radius: 7px 0 0 0;
        }

        &:last-child {
            border-radius: 0 7px 0 0;
        }

        &.col-desc {
            text-align: left;
        }

        &.col-price,
        &.col-discount,
        &.col-total {
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
        }

        &.col-actions {
            text-align: center;
        }
    }

    tbody td {
        min-width: 0;
        box-sizing: border-box;
        padding: 12px 4px;
        border-bottom: 1px solid #f1f5f9;
        font-size: 13px;
        text-align: center;

        &.col-desc {
            text-align: left;
        }

        &.col-price,
        &.col-discount,
        &.col-tva,
        &.col-total {
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &.col-actions {
            text-align: center;
        }
    }

    tbody tr.add-option-row .col-desc {
        grid-column: 2 / -1;
    }

    tbody td.table-add-line {
        grid-column: 1 / -1;
    }
}

/* Le contrôle de quantité (− / nombre / +) vit désormais dans components/QtyStepper.vue,
   qui porte son propre style scoped. Rien à styler ici. */

/* Crayon d'édition PAR CASE : masqué, révélé au survol de la case (td). Clic → la case seule
   passe en saisie et s'enregistre au blur / Entrée (cf. startCellEdit / saveCellEdit). */
.cell-with-pencil {
    position: relative;
    display: block;
    width: max-content;
    min-width: 0;
    cursor: pointer;
}
.cell-with-pencil:hover {
    text-decoration: underline dotted;
    color: #7d2dbd;
    text-underline-offset: 3px;
}
.cell-with-pencil.vat-rate-editable {
    cursor: pointer;
}
.cell-edit-pencil {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translate(100%, -50%);
    padding: 2px;
    border: 0;
    background: transparent;
    color: #7d2dbd;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease;
}
.cell-with-pencil:hover .cell-edit-pencil {
    opacity: 0.6;
}
.cell-edit-pencil:hover {
    opacity: 1;
}

/* Cellule TVA en cours d'édition. Le MENU n'est pas ici : BaseSelect le dépose dans <body>
   (fixed-dropdown) pour échapper au rognage de .table-responsive-wrapper / .main-canvas.
   Seule la case — largeur et centrage — est mise en forme ici. */
.vat-cell-edit {
    display: inline-flex;
    justify-content: center;
}

.devis-table td.vat-cell-editing {
    position: relative;
    z-index: 30;
}

.col-total-action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    text-align: center;
}

.row-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;

    .btn-row-action {
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.col-tva .cell-with-pencil {
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
}

.item-desc,
.item-desc > strong {
    width: 100%;
    min-width: 0;
}

.item-desc .cell-with-pencil {
    display: flex;
    align-items: center;
    gap: 6px;
    text-align: left;
}

.item-desc .cell-edit-pencil {
    position: static;
    flex: 0 0 auto;
    transform: none;
}

.item-name-value {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.col-optional {
    width: 64px;
    text-align: center;
}

/* Autocomplete de la ligne personnalisée : liste déroulante sous la désignation. */
.draft-suggest-wrap {
    position: relative;
}
.draft-suggest {
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    right: 0;
    z-index: 30;
    margin: 0;
    padding: 4px 0;
    list-style: none;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    max-height: 260px;
    overflow-y: auto;
}
.draft-suggest__head {
    padding: 4px 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #9ca3af;
}
.draft-suggest__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 12px;
    font-size: 13px;
    color: #1d1d1b;
    cursor: pointer;
}
.draft-suggest__item:hover {
    background: #f4e9fb;
    color: #7d2dbd;
}
.draft-suggest__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.draft-suggest__price {
    flex-shrink: 0;
    color: #6b7280;
    font-variant-numeric: tabular-nums;
}

.option-checkbox {
    accent-color: #7d2dbd;
    cursor: pointer;
}

/* Ligne proposée en option : exclue du total (recalcul serveur). Elle reste visible mais
   grisée, sauf sa propre case à cocher. */
.devis-table tbody tr.is-optional td:not(.col-optional) {
    color: #9ca3af;
    opacity: 0.6;
}

/* Ligne « + ajouter une option » sous la dernière option : discrète au repos, révélée au
   survol du groupe (tbody), pour rattacher une option de plus au même parent. */
.add-option-row td {
    padding-top: 0;
    padding-bottom: 4px;
}
.add-option-inline {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 18px;
    padding: 2px 8px;
    border: 1px dashed #cbb0e8;
    border-radius: 999px;
    background: transparent;
    color: #7d2dbd;
    font-size: 11px;
    font-family: 'Poppins Medium', sans-serif;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 0.15s ease,
        background 0.15s ease,
        border-color 0.15s ease;
}
.item-group:hover .add-option-inline {
    opacity: 1;
}
.add-option-inline:hover {
    background: #faf5ff;
    border-color: #7d2dbd;
}
.item-group:hover .add-option-inline:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

/* Action de note accolée au libellé : discrète au repos, révélée au survol de la ligne. */
.item-note-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 1px dashed #dfe4ec;
    border-radius: 9999px;
    background: transparent;
    color: #94a3b8;
    font-family: 'Poppins', sans-serif;
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 0.18s ease,
        border-color 0.18s ease,
        color 0.18s ease,
        background 0.18s ease;
}

.devis-table tr:hover .item-note-link,
.item-note-link:focus-visible,
.item-note-link.has-note {
    opacity: 1;
}

.item-note-link.has-note {
    border-color: rgba(125, 45, 189, 0.3);
    color: #7d2dbd;
}

.item-note-link:hover {
    border-color: #7d2dbd;
    background: #f0e2fb;
    color: #7d2dbd;
}

.item-note-text:hover {
    color: #7d2dbd;
    text-decoration-color: #7d2dbd;
}

.item-note-text {
    max-width: 100%;
    cursor: pointer;
    text-decoration: underline dotted transparent;
    text-underline-offset: 2px;
    transition:
        color 0.12s ease,
        text-decoration-color 0.12s ease;
}

.item-note-text:focus-visible {
    color: #7d2dbd;
    text-decoration-color: #7d2dbd;
}

.item-note-pencil {
    flex-shrink: 0;
    opacity: 0;
    transition: opacity 0.12s ease;
}

.item-note-text:hover .item-note-pencil,
.item-note-text:focus-visible .item-note-pencil {
    opacity: 0.75;
}

.item-note-input {
    box-sizing: border-box;
    min-width: 0;
    min-height: 25px;
    max-height: 96px;
    padding: 3px 8px;
    border: 1px solid #7d2dbd;
    border-radius: 5px;
    outline: none;
    background: #fff;
    color: #444c57;
    font-family: 'Poppins', sans-serif;
    font-size: 11.5px;
    line-height: 1.45;
    resize: none;
    overflow-y: auto;
}

.item-note-input::placeholder {
    color: #9aa4b2;
}

@media (hover: none) {
    .item-note-link {
        opacity: 1;
    }

    .item-note-pencil {
        opacity: 0.75;
    }
}

/* « + Ajouter une ligne », dernière ligne du tableau. Le sélecteur complet est
   indispensable : `.devis-table tbody td` (0,1,2) impose padding: 12px, une bordure
   basse et text-align: center, et l'emporterait sur un simple `.table-add-line` (0,1,0).
   Le td porte donc lui-même le padding, la bordure et l'alignement à droite — pas de
   display: flex, pour ne pas transformer une cellule en conteneur flex sous
   border-collapse: collapse. */
.devis-table tbody td.table-add-line {
    padding: 9px 20px;
    border-bottom: 1px solid #e2e6ec;
    text-align: right;

    .is-disabled {
        opacity: 0.5;
    }
}

.table-add-line__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 12.5px;
    font-weight: 500;
    color: #7d2dbd;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        cursor: not-allowed;
    }
    &span:hover {
        text-decoration: underline;
    }
}

.price-display {
    position: relative;

    &__trigger {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        height: 26px;
        padding: 0 10px;
        border: 1px solid #d1d5db;
        border-radius: 999px;
        font-size: 11.5px;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            border-color: #9ca3af;
            background: #f3f4f6;
            color: #1d1d1b;
        }
    }

    &__chevron {
        flex-shrink: 0;
        color: #9ca3af;
    }

    &__menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        z-index: 90;
        width: 150px;
        padding: 5px;
        border: 1px solid #e5e7eb;
        border-radius: 9px;
        background: #fff;
        box-shadow: 0 12px 28px rgb(2 24 50 / 16%);
    }

    &__option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        width: 100%;
        padding: 6px 9px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: #1d1d1b;
        font-size: 12.5px;
        font-weight: 400;
        text-align: left;
        cursor: pointer;

        &:hover,
        &.is-active {
            background: #f3eafa;
        }

        &.is-active {
            font-weight: 600;
        }
    }
}

.table-footer {
    display: flex;
    justify-content: flex-end;

    .btn-add-line-link {
        text-decoration: none;
    }

    .btn-add-line-link:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .input-wrap input:disabled {
        cursor: not-allowed;
        background: rgba(255, 255, 255, 0.05);
    }

    .table-totals {
        width: 415px;
        display: flex;
        flex-direction: column;
        gap: 8px;

        .total-row {
            display: flex;
            justify-content: space-between;
        }

        /* Filet fin de séparation avant le TOTAL TTC. */
        .total-divider {
            height: 1px;
            background: rgba(29, 29, 27, 0.12);
            margin: 2px 0;
        }

        /* Enrobe du bloc remise : reproduit l'empilement vertical que les éléments
           avaient directement dans .table-totals (colonne, gap 8px). Porte le tooltip
           de verrou (devis accepté) via .quote-locked-btn. */
        .discount-lock-wrap {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        /* État 1 : lien texte « Appliquer une remise ». */
        .discount-link {
            align-self: flex-end;
            background: none;
            border: 0;
            padding: 0;
            color: #7d2dbd;
            cursor: pointer;
            transition: opacity 0.15s ease;

            &:hover {
                text-decoration: underline;
                opacity: 0.85;
            }
        }

        /* Code promo APPLIQUÉ : intention affichée, jamais une déduction. Le label est
           cliquable pour rouvrir l'éditeur ; le × retire le code (persisté). */
        .discount-applied {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 6px;

            &__label {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                background: none;
                border: 0;
                padding: 0;
                color: #6b7280; /* gray-500 : label de la maquette */
                font-size: 12px;
                cursor: pointer;

                &:hover .discount-applied__code {
                    color: #7d2dbd;
                }
            }

            &__code {
                color: #374151; /* gray-700 */
                font-weight: 600;
            }

            &__value {
                color: #7d2dbd;
                font-weight: 600;
            }
        }

        /* État 2 : ligne d'édition de la remise, sur place. */
        .discount-editor {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 6px;

            &__cancel {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 20px;
                height: 20px;
                border: 0;
                background: none;
                color: #9ca3af; /* gray-400 */
                border-radius: 6px;
                cursor: pointer;
                transition:
                    background 0.15s ease,
                    color 0.15s ease;

                &:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }
            }

            &__controls {
                display: flex;
                align-items: center;
                gap: 6px;
                margin-left: auto;
            }
        }

        .discount-manual-or {
            color: #9ca3af;
            font-size: 11px;
        }

        .discount-manual-input {
            box-sizing: border-box;
            width: 46px;
            height: 26px;
            padding: 2px 7px;
            border: 1px solid #d1d5db;
            border-radius: 999px;
            background: #f3f4f6;
            color: #374151;
            font-size: 12px;
            text-align: center;
            appearance: textfield;

            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button {
                margin: 0;
                appearance: none;
            }

            &:focus {
                border-color: #7d2dbd;
                outline: none;
            }
        }

        .discount-manual-mode {
            display: inline-flex;
            align-items: center;
            gap: 2px;
            padding: 2px;
            border: 1px solid #d1d5db;
            border-radius: 999px;
            background: #f3f4f6;

            button {
                display: inline-flex;
                width: 22px;
                height: 20px;
                align-items: center;
                justify-content: center;
                padding: 0;
                border: 0;
                border-radius: 999px;
                background: transparent;
                color: #6b7280;
                cursor: pointer;
                font-size: 11px;
                font-weight: 600;

                &.is-active {
                    background: #fff;
                    color: #7d2dbd;
                    box-shadow: 0 1px 2px rgb(0 0 0 / 12%);
                }
            }
        }

        /* Pilule + popover des codes promo (remplace l'ancien <select>). */
        .discount-code {
            position: relative;

            &__pill {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                height: 26px;
                padding: 0 10px;
                border: 1px solid #d1d5db; /* gray-300 */
                border-radius: 999px;
                background: #fff;
                color: #6b7280; /* gray-500 */
                font-size: 12px;
                cursor: pointer;
                transition:
                    border-color 0.15s ease,
                    color 0.15s ease;

                &:hover {
                    border-color: #9ca3af; /* gray-400 */
                }

                /* Un code retenu s'affiche en Plusdark, comme dans la maquette. */
                &.is-active {
                    color: #374151; /* gray-700 */
                    border-color: #a7b5c8; /* secondary-300 */
                }
            }

            &__pill-label {
                max-width: 120px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &__chevron {
                flex-shrink: 0;
            }

            /* Le popover s'ouvre VERS LE HAUT (au-dessus de la ligne d'édition), comme dans
               la maquette : il n'y a pas de place en dessous dans le pied du tableau. */
            &__list {
                position: absolute;
                bottom: calc(100% + 6px);
                right: 0;
                z-index: 70;
                width: 236px;
                max-height: 300px;
                overflow-y: auto;
                padding: 6px;
                background: #fff;
                border: 1px solid #e5e7eb; /* gray-200 */
                border-radius: 9px;
                box-shadow:
                    0 10px 15px -3px rgb(0 0 0 / 10%),
                    0 4px 6px -4px rgb(0 0 0 / 10%);
            }

            &__group + &__group {
                margin-top: 6px;
                padding-top: 6px;
                border-top: 1px solid #f3f4f6; /* gray-100 */
            }

            &__group-label {
                margin: 0 0 4px;
                padding: 0 6px;
                color: #9ca3af; /* gray-400 */
                font-size: 10px;
                font-weight: 600;
                letter-spacing: 0.06em;
                text-transform: uppercase;
            }

            &__item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                width: 100%;
                padding: 6px;
                border: 0;
                border-radius: 6px;
                background: none;
                text-align: left;
                cursor: pointer;

                &:hover {
                    background: #f3f4f6; /* gray-100 */
                }

                &.is-selected {
                    background: #f3eafa; /* primary-100 */
                }
            }

            &__item-label {
                color: #374151; /* gray-700 */
                font-size: 12px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            &__item-value {
                flex-shrink: 0;
                color: #7d2dbd;
                font-size: 11.5px;
                font-weight: 600;
            }
        }

        /* Encadré « Demander un acompte ». */
        .deposit-box {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            width: 100%;
            margin-top: 4px;
            padding: 8px 11px;
            border: 0;
            border-radius: 8px;
            background: #954fd333;
            cursor: pointer;
            text-align: left;
            transition:
                border-color 0.15s ease,
                background 0.15s ease;

            &:hover {
                background: #ead9f7;
            }

            &__text {
                display: flex;
                flex-direction: column;
                line-height: 1.3;
            }

            &__title {
                color: #7d2dbd;
                font-size: 12px;
            }

            &__subtitle {
                color: #a06fce;
                font-size: 10.5px;
            }

            &__chevron {
                color: #7d2dbd;
                flex-shrink: 0;
            }
        }

        .deposit-editor {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-top: 2px;
            padding: 1px 0;

            &__spacer {
                flex: 1 1 auto;
            }

            &__input {
                box-sizing: border-box;
                width: 56px;
                height: 26px;
                padding: 0 7px;
                border: 1px solid #d1d5db;
                border-radius: 7px;
                background: #fff;
                color: #374151;
                font-size: 13px;
                text-align: right;
                appearance: textfield;

                &::-webkit-inner-spin-button,
                &::-webkit-outer-spin-button {
                    margin: 0;
                    appearance: none;
                }

                &:focus {
                    border-color: #7d2dbd;
                    outline: none;
                }
            }
        }
    }
}

/* Canvas Footer */
.canvas-footer {
    .footer-section {
        h5 {
            font-size: 12px;
            font-weight: 800;
            color: #f45b69 !important;
            margin-bottom: 12px;
        }

        p {
            font-size: clamp(12px, 0.729545vw, 16px);
            /*pour taille 14*/
            line-height: 1.6;
            margin: 0;
        }
    }
}

.header-template-selector {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;

    label {
        font-size: 11px;
        font-weight: 700;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    select {
        width: 100%;
        padding: 8px 10px;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        font-size: 13px;
        background: #fff;
        cursor: pointer;
        color: #2d3748;
        outline: none;
        transition: all 0.2s ease;

        &:hover {
            border-color: #cbd5e0;
        }

        &:focus {
            border-color: #7c4dff;
            box-shadow: 0 0 0 2px rgba(124, 77, 255, 0.1);
        }
    }
}

.gripHandle {
    opacity: 0;
    transition: opacity 0.15s ease;
    color: #94a3b8;
}

tr:hover .gripHandle {
    opacity: 1;
}

tr.ppv2-bg-purple-100 .gripHandle,
tbody.ppv2-bg-purple-100 .gripHandle {
    opacity: 1;
}

/* Ligne conteneur (prestation simpleTicket) : enregistrée à 0 €, elle n'affiche aucun
   montant — verrouillée, non supprimable directement. */
.devis-table tbody tr.is-container .item-desc strong {
    color: #475569;
}

.containerLock {
    color: #94a3b8;
    flex: 0 0 auto;
}

/* Lignes incluses (billets d'un simpleTicket, options) : rattachées visuellement à leur
   ligne principale, non déplaçables, mais porteuses des montants et supprimables. */
.devis-table tbody tr.is-child td {
    border-bottom: none;
    padding-top: 5px;
    padding-bottom: 5px;
}

.devis-table tbody.item-group tr.is-child:last-child td {
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 12px;
}

.child-desc {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 28px;
}

.child-bullet {
    color: #94a3b8;
    line-height: 1;
}

.col-grip {
    width: 40px;
    min-width: 40px;
    max-width: 40px;
}

.col-grip.ppv2-cursor-grab {
    cursor: grab;
}

.col-grip.ppv2-cursor-grab:active {
    cursor: grabbing;
}
.item-note-editor {
    width: max-content !important;
}
</style>
